<template>
  <Teleport to="body">
    <!-- Backdrop with blur effect - z-index 9999 to be on top of everything -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4"
      @click.self="currentStep === 'complete' ? handleClose : null"
    >
      <!-- Modal Container - Mobile Responsive -->
      <div
        class="bg-white rounded-xl sm:rounded-2xl shadow-[4px_4px_0_0_rgba(0,0,0,1)] sm:shadow-[8px_8px_0_0_rgba(0,0,0,1)] border-2 sm:border-3 border-black w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
        @click.stop
      >
        <!-- Header - Mobile Responsive -->
        <div
          class="bg-gradient-to-r from-blue-500 to-blue-600 p-3 sm:p-6 border-b-2 sm:border-b-3 border-black"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 sm:gap-3">
              <div
                class="bg-white rounded-full p-2 sm:p-3 border-2 border-black shadow-[2px_2px_0_0_rgba(0,0,0,1)] sm:shadow-[4px_4px_0_0_rgba(0,0,0,1)]"
              >
                <svg
                  class="w-5 h-5 sm:w-6 sm:h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              </div>
              <div>
                <h2 class="text-lg sm:text-2xl font-bold text-white">Pre-Session Assessment</h2>
                <p class="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1">{{ sessionSubject }}</p>
              </div>
            </div>

            <!-- Exit Button (only show before test completion and during intro/ready steps) -->
            <button
              v-if="currentStep === 'intro' || currentStep === 'ready'"
              @click="handleExitRequest"
              class="bg-white hover:bg-gray-100 text-gray-700 rounded-lg px-3 py-2 text-sm font-semibold border-2 border-black shadow-[2px_2px_0_0_rgba(0,0,0,1)] transition-all duration-200 hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] flex items-center gap-2 cursor-pointer"
              :disabled="isGenerating"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span class="hidden sm:inline">Go Back</span>
            </button>
          </div>
        </div>

        <!-- Content - Mobile Responsive -->
        <div class="flex-1 overflow-y-auto p-3 sm:p-6">
          <!-- Introduction Step -->
          <div v-if="currentStep === 'intro'" class="space-y-6">
            <div class="bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
              <h3 class="text-xl font-bold text-gray-900 mb-4">📝 AI-Generated Pre-Test</h3>
              <p class="text-gray-700 mb-4">
                Before your tutoring session, we'll create a personalized assessment to understand
                your current knowledge level.
              </p>
              <div class="space-y-3">
                <div class="flex items-start gap-3">
                  <div
                    class="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold"
                  >
                    ✓
                  </div>
                  <p class="text-gray-700"><strong>Subject:</strong> {{ sessionSubject }}</p>
                </div>
                <div v-if="learningGoals" class="flex items-start gap-3">
                  <div
                    class="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold"
                  >
                    ✓
                  </div>
                  <p class="text-gray-700"><strong>Learning Goals:</strong> {{ learningGoals }}</p>
                </div>
                <div v-if="materials && materials.length > 0" class="flex items-start gap-3">
                  <div
                    class="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold"
                  >
                    ✓
                  </div>
                  <p class="text-gray-700">
                    <strong>Uploaded Materials:</strong> {{ materials[0].name }}
                  </p>
                </div>
              </div>
            </div>

            <div class="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
              <div class="flex items-start gap-3">
                <svg
                  class="w-6 h-6 text-amber-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <p class="text-sm font-semibold text-amber-900 mb-1">Why This Matters</p>
                  <p class="text-sm text-amber-800">
                    This pre-test helps your tutor understand what you already know, so they can
                    focus on areas where you need the most help.
                  </p>
                </div>
              </div>
            </div>

            <button
              @click="startAssessment"
              class="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-xl border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all cursor-pointer"
            >
              Start Pre-Test Preparation
            </button>
          </div>

          <!-- Phase 1 Pipeline Progress - Enhanced -->
          <div
            v-else-if="
              currentStep === 'extracting' ||
              currentStep === 'validating' ||
              currentStep === 'language-detection'
            "
            class="py-6 sm:py-12 px-4"
          >
            <!-- Animated Icon Container -->
            <div class="flex justify-center mb-6">
              <div class="relative">
                <!-- Outer rotating ring -->
                <div
                  class="absolute inset-0 rounded-full border-4 border-blue-200 animate-spin-slow"
                ></div>
                <!-- Inner pulsing circle -->
                <div
                  class="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg animate-pulse-slow"
                >
                  <!-- Icon changes based on step -->
                  <svg
                    v-if="currentStep === 'extracting'"
                    class="w-10 h-10 sm:w-12 sm:h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <svg
                    v-else-if="currentStep === 'validating'"
                    class="w-10 h-10 sm:w-12 sm:h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="w-10 h-10 sm:w-12 sm:h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Step Title -->
            <h3 class="text-xl sm:text-2xl font-bold text-gray-900 mb-2 text-center">
              {{
                currentStep === "extracting"
                  ? "Extracting Content"
                  : currentStep === "validating"
                  ? "Validating Content"
                  : "Detecting Language"
              }}
            </h3>

            <!-- Step Description -->
            <p class="text-sm sm:text-base text-gray-600 mb-6 text-center max-w-md mx-auto">
              {{
                currentStep === "extracting"
                  ? "Reading and processing your uploaded materials"
                  : currentStep === "validating"
                  ? "Checking content for safety and academic quality"
                  : "Analyzing the language of your materials"
              }}
            </p>

            <!-- Enhanced Progress Bar with Gradient -->
            <div class="max-w-2xl mx-auto mb-4">
              <div
                class="relative h-3 sm:h-4 bg-gray-200 rounded-full overflow-hidden border-2 border-gray-300"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 transition-all duration-700 ease-out"
                  :style="{
                    width:
                      currentStep === 'extracting'
                        ? '33%'
                        : currentStep === 'validating'
                        ? '66%'
                        : '100%',
                  }"
                >
                  <!-- Animated shimmer effect -->
                  <div
                    class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
                  ></div>
                </div>
              </div>

              <!-- Progress Info -->
              <div class="flex justify-between items-center mt-3 text-xs sm:text-sm">
                <span class="font-semibold text-gray-700"> Phase 1: Content Preparation </span>
                <span class="font-bold text-blue-600">
                  {{
                    currentStep === "extracting"
                      ? "Step 1/3"
                      : currentStep === "validating"
                      ? "Step 2/3"
                      : "Step 3/3"
                  }}
                </span>
              </div>
            </div>

            <!-- Estimated Time -->
            <div class="text-center">
              <div
                class="inline-flex items-center gap-2 bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-2 text-xs sm:text-sm"
              >
                <svg
                  class="w-4 h-4 text-blue-600 animate-pulse"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span class="text-gray-700">
                  <span class="font-semibold">Estimated:</span>
                  {{
                    currentStep === "extracting"
                      ? "5-10 seconds"
                      : currentStep === "validating"
                      ? "10-15 seconds"
                      : "5-8 seconds"
                  }}
                </span>
              </div>
            </div>

            <!-- Step Details (What's happening) -->
            <div class="mt-6 max-w-2xl mx-auto">
              <div
                class="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 sm:p-5"
              >
                <p class="text-xs sm:text-sm font-semibold text-blue-900 mb-3">
                  What's happening now:
                </p>
                <ul class="space-y-2 text-xs sm:text-sm text-gray-700">
                  <li
                    v-if="currentStep === 'extracting'"
                    class="flex items-start gap-2 animate-fade-in"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Parsing document structure and extracting text content</span>
                  </li>
                  <li
                    v-if="currentStep === 'extracting'"
                    class="flex items-start gap-2 animate-fade-in"
                    style="animation-delay: 0.1s"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Converting PDF, DOCX, or TXT files to readable format</span>
                  </li>
                  <li
                    v-if="currentStep === 'extracting'"
                    class="flex items-start gap-2 animate-fade-in"
                    style="animation-delay: 0.2s"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Cleaning and organizing extracted information</span>
                  </li>

                  <li
                    v-if="currentStep === 'validating'"
                    class="flex items-start gap-2 animate-fade-in"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Scanning for inappropriate or unsafe content</span>
                  </li>
                  <li
                    v-if="currentStep === 'validating'"
                    class="flex items-start gap-2 animate-fade-in"
                    style="animation-delay: 0.1s"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Verifying content matches the selected subject</span>
                  </li>
                  <li
                    v-if="currentStep === 'validating'"
                    class="flex items-start gap-2 animate-fade-in"
                    style="animation-delay: 0.2s"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Checking academic quality and educational value</span>
                  </li>

                  <li
                    v-if="currentStep === 'language-detection'"
                    class="flex items-start gap-2 animate-fade-in"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Identifying primary language (English or Filipino)</span>
                  </li>
                  <li
                    v-if="currentStep === 'language-detection'"
                    class="flex items-start gap-2 animate-fade-in"
                    style="animation-delay: 0.1s"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Analyzing text patterns and linguistic features</span>
                  </li>
                  <li
                    v-if="currentStep === 'language-detection'"
                    class="flex items-start gap-2 animate-fade-in"
                    style="animation-delay: 0.2s"
                  >
                    <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Preparing language-appropriate question generation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Phase 2: Generating AI Questions with Enhanced 13-Step Progress -->
          <div v-else-if="currentStep === 'generating'" class="py-6 sm:py-12 px-4">
            <!-- Animated AI Brain Icon -->
            <div class="flex justify-center mb-6">
              <div class="relative">
                <!-- Outer pulsing ring -->
                <div class="absolute inset-0 rounded-full bg-purple-400/30 animate-ping"></div>
                <!-- Middle rotating ring -->
                <div
                  class="absolute inset-2 rounded-full border-4 border-purple-300 animate-spin-slow"
                ></div>
                <!-- Inner gradient circle -->
                <div
                  class="relative w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 rounded-full flex items-center justify-center shadow-xl"
                >
                  <!-- Sparkling effect -->
                  <div class="absolute inset-0 rounded-full overflow-hidden">
                    <div
                      class="absolute top-0 right-0 w-12 h-12 bg-white/30 rounded-full -translate-y-1/2 translate-x-1/2 animate-pulse"
                    ></div>
                    <div
                      class="absolute bottom-0 left-0 w-8 h-8 bg-white/20 rounded-full translate-y-1/2 -translate-x-1/2 animate-pulse"
                      style="animation-delay: 0.5s"
                    ></div>
                  </div>
                  <!-- AI Icon -->
                  <svg
                    class="w-12 h-12 sm:w-14 sm:h-14 text-white relative z-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Current Step Message -->
            <h3
              class="text-xl sm:text-2xl font-bold text-gray-900 mb-2 text-center animate-fade-in"
            >
              {{ currentAIStepMessage || "Generating Pre-Test Questions" }}
            </h3>

            <!-- Subject Badge -->
            <div class="flex justify-center mb-6">
              <div
                class="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 rounded-full px-4 py-2"
              >
                <svg class="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"
                  />
                </svg>
                <span class="text-sm sm:text-base font-semibold text-gray-800">{{
                  sessionSubject
                }}</span>
              </div>
            </div>

            <!-- Enhanced Progress Bar with Step Indicator -->
            <div class="max-w-3xl mx-auto mb-6">
              <!-- Progress Bar -->
              <div
                class="relative h-4 sm:h-5 bg-gray-200 rounded-full overflow-hidden border-2 border-gray-300 shadow-inner"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-r from-purple-500 via-purple-600 to-indigo-600 transition-all duration-700 ease-out"
                  :style="{ width: `${aiProgress}%` }"
                >
                  <!-- Animated shimmer -->
                  <div
                    class="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"
                  ></div>
                  <!-- Pulse effect at the end -->
                  <div class="absolute right-0 top-0 bottom-0 w-1 bg-white animate-pulse"></div>
                </div>
              </div>

              <!-- Progress Stats -->
              <div class="flex justify-between items-center mt-3">
                <div class="text-left">
                  <p class="text-xs sm:text-sm font-semibold text-gray-700">
                    Phase 2: AI Generation
                  </p>
                  <p class="text-xs text-gray-500">Step {{ currentAIStep }} of 13</p>
                </div>
                <div class="text-right">
                  <p class="text-lg sm:text-xl font-bold text-purple-600">
                    {{ Math.round(aiProgress) }}%
                  </p>
                  <p class="text-xs text-gray-500">Complete</p>
                </div>
              </div>
            </div>

            <!-- Time Estimate -->
            <div class="flex justify-center mb-6">
              <div
                class="inline-flex items-center gap-2 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg px-4 py-2.5 shadow-sm"
              >
                <div class="relative">
                  <svg
                    class="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span
                    class="absolute top-0 right-0 w-2 h-2 bg-amber-500 rounded-full animate-ping"
                  ></span>
                </div>
                <div class="text-sm">
                  <span class="font-semibold text-gray-700">Estimated Time:</span>
                  <span class="ml-1 font-bold text-amber-700">40-60 seconds</span>
                </div>
              </div>
            </div>

            <!-- Step Details Panel -->
            <div class="max-w-3xl mx-auto">
              <div
                class="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 border-2 border-purple-200 rounded-xl p-4 sm:p-6 shadow-lg"
              >
                <!-- AI Model Badge -->
                <div
                  class="flex items-center justify-center gap-2 mb-4 pb-4 border-b-2 border-purple-200"
                >
                  <div
                    class="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md"
                  >
                    <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 7H7v6h6V7z" />
                      <path
                        fill-rule="evenodd"
                        d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <div class="text-left">
                    <p class="text-xs text-purple-600 font-semibold">Powered by</p>
                    <p class="text-sm font-bold text-gray-900">Gemini 2.5-Flash AI</p>
                  </div>
                </div>

                <!-- Current Process Details -->
                <div class="space-y-3">
                  <p class="text-xs sm:text-sm font-semibold text-purple-900 mb-2">
                    Current Process:
                  </p>

                  <!-- Step-specific details -->
                  <div class="space-y-2.5 text-xs sm:text-sm text-gray-700">
                    <div
                      v-if="currentAIStep >= 5 && currentAIStep < 6"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span
                        >Scanning content for duplicate materials from previous assessments</span
                      >
                    </div>
                    <div
                      v-if="currentAIStep >= 6 && currentAIStep < 7"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Identifying key concepts and topics from your materials</span>
                    </div>
                    <div
                      v-if="currentAIStep >= 7 && currentAIStep < 7.5"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Searching educational databases for reliable reference sources</span>
                    </div>
                    <div
                      v-if="currentAIStep >= 7.5 && currentAIStep < 7.75"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Using AI reranking to select most relevant educational sources</span>
                    </div>
                    <div
                      v-if="currentAIStep >= 7.75 && currentAIStep < 8"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Verifying source URLs are accessible and valid</span>
                    </div>
                    <div
                      v-if="currentAIStep >= 8 && currentAIStep < 9"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Extracting factual information from credible educational websites</span>
                    </div>
                    <div
                      v-if="currentAIStep >= 9 && currentAIStep < 10"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span
                        >Delegating question generation to Gemini AI with fact-checking data</span
                      >
                    </div>
                    <div
                      v-if="currentAIStep >= 10 && currentAIStep < 11"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>AI is analyzing your content and formulating relevant questions</span>
                    </div>
                    <div
                      v-if="currentAIStep >= 11 && currentAIStep < 12"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Creating multiple-choice options with varying difficulty levels</span>
                    </div>
                    <div
                      v-if="currentAIStep >= 12 && currentAIStep < 13"
                      class="flex items-start gap-2 animate-fade-in"
                    >
                      <div
                        class="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Finalizing questions with detailed explanations for each answer</span>
                    </div>
                    <div v-if="currentAIStep >= 13" class="flex items-start gap-2 animate-fade-in">
                      <div
                        class="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"
                      ></div>
                      <span>Validating question quality against 7 educational standards</span>
                    </div>
                  </div>
                </div>

                <!-- Quality Assurance Note -->
                <div class="mt-4 pt-4 border-t-2 border-purple-200">
                  <div class="flex items-start gap-2">
                    <svg
                      class="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <div class="text-xs sm:text-sm text-purple-900">
                      <p class="font-semibold mb-1">Quality Assurance:</p>
                      <p class="text-purple-800">
                        Questions are fact-checked against credible sources and validated for
                        educational quality
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Phase 2: Question Display -->
          <div v-else-if="currentStep === 'questions'" class="space-y-4 sm:space-y-6">
            <div
              class="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 rounded-xl p-3 sm:p-6"
            >
              <h3 class="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
                📝 Pre-Test Questions
              </h3>
              <p class="text-gray-700 text-xs sm:text-sm">
                Question {{ currentQuestion + 1 }} of {{ testQuestions.length }}
              </p>
            </div>

            <!-- ⚡ Loading skeleton while rendering LaTeX -->
            <div
              v-if="isRenderingQuestions"
              class="bg-white border-2 border-gray-200 rounded-xl p-3 sm:p-6 animate-pulse"
            >
              <div class="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div class="bg-gray-300 rounded-full w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div class="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
              <div class="space-y-2 sm:space-y-3">
                <div v-for="i in 4" :key="i" class="h-12 sm:h-16 bg-gray-200 rounded-lg"></div>
              </div>
            </div>

            <!-- ⚡ Actual questions (rendered after caching complete) -->
            <div v-else class="bg-white border-2 border-gray-200 rounded-xl p-3 sm:p-6">
              <div class="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div
                  class="bg-purple-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0 font-bold text-sm sm:text-base"
                >
                  {{ currentQuestion + 1 }}
                </div>
                <h3
                  class="text-base sm:text-lg font-bold text-gray-900 question-latex"
                  v-html="currentRenderedQuestion?.question || ''"
                ></h3>
              </div>

              <!-- Multiple Choice Options - Mobile Responsive -->
              <div class="space-y-2 sm:space-y-3">
                <button
                  v-for="(option, index) in currentRenderedQuestion?.options || []"
                  :key="index"
                  @click="selectAnswer(index)"
                  :class="[
                    'w-full text-left py-3 px-4 sm:py-4 sm:px-6 rounded-lg sm:rounded-xl border-2 border-black font-medium transition-all cursor-pointer text-sm sm:text-base option-latex',
                    'shadow-[2px_2px_0_0_rgba(0,0,0,1)] sm:shadow-[4px_4px_0_0_rgba(0,0,0,1)]',
                    'active:shadow-none active:translate-x-[2px] active:translate-y-[2px]',
                    answers[currentQuestion] === index
                      ? 'bg-purple-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100',
                  ]"
                >
                  <span class="font-bold mr-2">{{ String.fromCharCode(65 + index) }}.</span>
                  <span v-html="option"></span>
                </button>
              </div>
            </div>

            <!-- Progress Bar - Fixed: Shows completed questions, not current question -->
            <div class="space-y-2">
              <div class="flex justify-between text-sm text-gray-600">
                <span>Progress</span>
                <span>{{ Math.round((currentQuestion / testQuestions.length) * 100) }}%</span>
              </div>
              <div
                class="w-full bg-gray-200 rounded-full h-3 border-2 border-black overflow-hidden"
              >
                <div
                  class="bg-purple-500 h-full transition-all duration-300"
                  :style="{ width: `${(currentQuestion / testQuestions.length) * 100}%` }"
                ></div>
              </div>
            </div>
          </div>

          <!-- Completion Step - Review Screen (matches PostTest) -->
          <div v-else-if="currentStep === 'complete'" class="space-y-6">
            <!-- Score Display Header -->
            <div class="text-center">
              <div
                class="w-28 h-28 rounded-2xl mx-auto mb-4 flex items-center justify-center relative overflow-hidden shadow-lg border-3 bg-gradient-to-br from-purple-400 to-indigo-500 border-purple-600"
              >
                <div class="absolute inset-0 opacity-20">
                  <div
                    class="absolute top-0 right-0 w-24 h-24 bg-white rounded-full -translate-y-1/2 translate-x-1/2"
                  ></div>
                  <div
                    class="absolute bottom-0 left-0 w-16 h-16 bg-white rounded-full translate-y-1/2 -translate-x-1/2"
                  ></div>
                </div>
                <span class="text-5xl relative z-10">🎯</span>
              </div>

              <div
                class="inline-block px-6 py-2 rounded-xl border-2 shadow-md mb-3 bg-purple-100 border-purple-600"
              >
                <h3 class="text-2xl font-bold text-purple-700">Pre-Test Complete! 🎉</h3>
              </div>

              <div
                class="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 border-2 border-purple-200 shadow-sm mb-4 max-w-2xl mx-auto"
              >
                <div class="flex items-center justify-center gap-6 mb-3">
                  <div class="text-center">
                    <div
                      class="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600"
                    >
                      {{ Math.round((calculateScore() / testQuestions.length) * 100) }}%
                    </div>
                    <div class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Your Score
                    </div>
                  </div>
                  <div class="w-px h-16 bg-purple-300"></div>
                  <div class="text-center">
                    <div class="text-3xl font-black text-gray-900">
                      {{ calculateScore()
                      }}<span class="text-xl text-gray-500">/{{ testQuestions.length }}</span>
                    </div>
                    <div class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Correct Answers
                    </div>
                  </div>
                </div>

                <p class="text-gray-700 text-sm font-medium leading-relaxed">
                  Thank you for completing the pre-session assessment! Your responses will help your
                  tutor understand your current knowledge level and provide the best learning
                  experience. Review your answers below to identify areas for improvement. 📚
                </p>
              </div>
            </div>

            <!-- Review Answers Section (matches PostTest) -->
            <div class="bg-white rounded-xl p-5 mb-5 border-2 border-gray-300 shadow-sm">
              <div class="flex items-center gap-2.5 mb-4">
                <div class="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <svg
                    class="w-5 h-5 text-indigo-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                </div>
                <h4 class="text-lg font-bold text-gray-900">Review Your Answers</h4>
              </div>

              <div class="space-y-3">
                <div
                  v-for="(question, index) in testQuestions"
                  :key="index"
                  v-memo="[answers[index], renderedQuestionsCache[index]]"
                  :class="[
                    'border-l-4 pl-4 py-3 rounded-r-lg transition-all',
                    answers[index] === question.correctAnswer
                      ? 'border-green-500 bg-green-50/50'
                      : 'border-red-500 bg-red-50/50',
                  ]"
                >
                  <div class="flex items-start gap-2.5 mb-2">
                    <div
                      :class="[
                        'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-sm',
                        answers[index] === question.correctAnswer
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800',
                      ]"
                    >
                      {{ index + 1 }}
                    </div>
                    <p
                      class="text-sm font-semibold text-gray-900 leading-relaxed flex-1"
                      v-html="renderedQuestionsCache[index]?.question"
                    ></p>
                    <div
                      :class="[
                        'w-8 h-8 rounded-lg flex items-center justify-center',
                        answers[index] === question.correctAnswer ? 'bg-green-500' : 'bg-red-500',
                      ]"
                    >
                      <svg
                        v-if="answers[index] === question.correctAnswer"
                        class="w-5 h-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-5 h-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>

                  <div class="ml-9 space-y-1.5">
                    <div class="flex items-start gap-2">
                      <span class="text-xs font-semibold text-gray-600 flex-shrink-0 mt-0.5"
                        >Your answer:</span
                      >
                      <span
                        :class="[
                          'text-xs font-semibold px-2.5 py-1 rounded-md',
                          answers[index] === question.correctAnswer
                            ? 'bg-green-200 text-green-900'
                            : 'bg-red-200 text-red-900',
                        ]"
                        v-html="renderedQuestionsCache[index]?.options[answers[index]]"
                      ></span>
                    </div>

                    <div
                      v-if="answers[index] !== question.correctAnswer"
                      class="flex items-start gap-2"
                    >
                      <span class="text-xs font-semibold text-gray-600 flex-shrink-0 mt-0.5"
                        >Correct answer:</span
                      >
                      <span
                        class="text-xs font-semibold px-2.5 py-1 rounded-md bg-green-200 text-green-900"
                        v-html="renderedQuestionsCache[index]?.options[question.correctAnswer]"
                      ></span>
                    </div>

                    <div class="bg-white rounded-lg p-3 mt-2 border border-gray-200">
                      <div class="flex items-start gap-2">
                        <svg
                          class="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p
                          class="text-xs text-gray-700 leading-relaxed"
                          v-html="renderedQuestionsCache[index]?.explanation"
                        ></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Motivational Message -->
            <div class="bg-blue-50 border-2 border-blue-300 rounded-xl p-5 text-center">
              <p class="text-sm text-gray-700 font-medium">
                ✨ <strong>What's Next:</strong> Your tutor will use this baseline assessment to
                tailor the session to your needs. After the session, you'll complete a post-test to
                measure your learning progress. Keep up the great work!
              </p>
            </div>
          </div>
        </div>

        <!-- Footer - Mobile Responsive -->
        <div class="border-t-2 border-gray-200 p-3 sm:p-6 bg-gray-50">
          <div class="flex justify-between items-center gap-2">
            <!-- Back Button (Questions) -->
            <button
              v-if="currentStep === 'questions' && currentQuestion > 0"
              @click="previousQuestion"
              class="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold rounded-xl border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all"
            >
              ← Previous
            </button>
            <div v-else></div>

            <!-- Next Button (Questions) -->
            <button
              v-if="currentStep === 'questions' && currentQuestion < testQuestions.length - 1"
              @click="nextQuestion"
              :disabled="!isCurrentQuestionAnswered"
              :class="[
                'px-6 py-3 font-bold rounded-xl border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] transition-all',
                isCurrentQuestionAnswered
                  ? 'bg-purple-500 hover:bg-purple-600 text-white hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed',
              ]"
            >
              Next →
            </button>

            <!-- Submit Button (Last Question) -->
            <button
              v-else-if="currentStep === 'questions'"
              @click="submitPreTest"
              :disabled="!isCurrentQuestionAnswered || isSubmitting"
              :class="[
                'px-6 py-3 font-bold rounded-xl border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] transition-all flex items-center gap-2',
                isCurrentQuestionAnswered && !isSubmitting
                  ? 'bg-green-500 hover:bg-green-600 text-white hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed',
              ]"
            >
              <svg
                v-if="isSubmitting"
                class="animate-spin h-5 w-5 text-white inline"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{{ isSubmitting ? "Submitting..." : "Submit Pre-Test" }}</span>
            </button>

            <!-- Close Button (Complete) -->
            <button
              v-if="currentStep === 'complete'"
              @click="handleClose"
              :disabled="isNavigating"
              :class="[
                'w-full font-bold py-4 px-6 rounded-xl border-2 border-black transition-all flex items-center justify-center gap-2',
                isNavigating
                  ? 'bg-gray-400 cursor-not-allowed opacity-70'
                  : 'bg-green-500 hover:bg-green-600 shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] cursor-pointer',
                'text-white',
              ]"
            >
              <!-- Loading Spinner -->
              <svg
                v-if="isNavigating"
                class="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{{
                isNavigating
                  ? "Redirecting to Confirmation..."
                  : "Continue to Appointment Confirmation"
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, shallowRef } from "vue";
import { auth, db } from "@/firebase";
import { doc, setDoc, Timestamp, getDoc } from "firebase/firestore";
import { ref as storageRef, uploadString, getDownloadURL } from "firebase/storage";
import { logDebug, logInfo, logWarn, logError, logSuccess } from "@/utils/secureLogger";
import { renderLatex } from "@/utils/latexRenderer";
import { geminiService } from "@/services/geminiService";
import Swal from "sweetalert2";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";

const PRETEST_STORAGE_KEY = "elevated_pending_pretest";

// ✅ Validator Detection: Validators can take unlimited pre-tests without overwriting
const isValidator = ref(false);

async function checkUserType() {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      isValidator.value = false;
      return;
    }

    const userDocRef = doc(db, "users", currentUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      isValidator.value = userData.userType === "validator";
      logDebug("User type detected in PreTestModal", {
        isValidator: isValidator.value,
        userType: userData.userType,
      });
    } else {
      isValidator.value = false;
    }
  } catch (error) {
    logError("Error checking user type in PreTestModal", error);
    isValidator.value = false;
  }
}

interface Props {
  isOpen: boolean;
  sessionId: string;
  sessionSubject: string;
  learningGoals?: string; // Learning goals from appointment
  isMandatory?: boolean; // True for admin-created sessions (cannot be dismissed)
  materials?: Array<{
    name: string;
    url: string;
    type: string;
    originalFile?: File; // ✅ FIX: Original File object to avoid CORS issues
  }>; // Uploaded files from appointment
}

const props = withDefaults(defineProps<Props>(), {
  isMandatory: false,
});

const emit = defineEmits<{
  complete: [data: any];
  close: [];
}>();

// State - Phase 1 & 2 Pipeline States
const currentStep = ref<
  | "intro"
  | "extracting"
  | "validating"
  | "language-detection"
  | "ready"
  | "generating"
  | "questions"
  | "complete"
>("intro");
const isGenerating = ref(false);
const isSubmitting = ref(false);
const isNavigating = ref(false); // ✅ NEW: Track navigation loading state

// Phase 1 validated context
const extractedContent = ref("");
const contentLanguage = ref<"english" | "filipino" | "mixed" | "unknown">("unknown");
const contentSafetyPassed = ref(false);
const validationErrors = ref<string[]>([]);

// Phase 2 (future) - AI-generated test state
const testQuestions = ref<
  Array<{
    question: string;
    options: string[];
    correctAnswer: number;
    explanation: string;
  }>
>([]);
const currentQuestion = ref(0);
const answers = ref<number[]>([]);
const testTopic = ref("");
const testSubject = ref("");
const testSources = ref<string[]>([]);

// ✅ Store completed test data for later emission (shallowRef for performance)
const completedPreTestData = shallowRef<any>(null);

// Pipeline metrics tracking
const phase1StartTime = ref("");
const phase1EndTime = ref("");
const phase2StartTime = ref("");
const phase2EndTime = ref("");
const extractionMethod = ref<"pdf" | "docx" | "txt" | "context-only">("context-only");
const languageConfidence = ref(0);

// Quality validation results storage
const qualityValidationResults = ref<{
  passed: boolean;
  score: number;
  passedStandards: string[];
  failures: string[];
} | null>(null);

// Rate limiting state for test attempts
const failedTestAttempts = ref(0);
const lastTestAttemptTime = ref(0);

// Progressive delay calculation for failed test attempts (30s, 1m, 5m, 15m, 30m)
const calculateTestAttemptDelay = (attempts: number): number => {
  const delays = [0, 30000, 60000, 300000, 900000, 1800000]; // milliseconds
  return delays[Math.min(attempts, delays.length - 1)];
};

// ✅ Maximum retry attempts for test generation (matches PostTestModal)
const MAX_RETRIES = 3;

// Reset failed attempts on successful test completion
const resetFailedAttempts = () => {
  failedTestAttempts.value = 0;
  lastTestAttemptTime.value = 0;
};

// Record failed test attempt
const recordFailedTestAttempt = () => {
  // Increment attempts but never exceed MAX_RETRIES
  if (failedTestAttempts.value < MAX_RETRIES) {
    failedTestAttempts.value = failedTestAttempts.value + 1;
  } else {
    failedTestAttempts.value = MAX_RETRIES;
  }

  // Always update last attempt timestamp
  lastTestAttemptTime.value = Date.now();
};

// 13-step progress tracking (matches PostTestModal)
const currentAIStep = ref(0);
const currentAIStepMessage = ref("");
const aiProgress = ref(0);

// Source pipeline analytics storage
const sourceAnalytics = ref<{
  searchResults: {
    totalSources: number;
    rerankedSources: number;
    topRelevanceScore: number;
    avgRelevanceScore: number;
    filteredOutCount: number;
  };
  urlValidation: {
    totalUrls: number;
    validUrls: number;
    invalidUrls: number;
    timeoutUrls: number;
  };
  scraping: {
    attemptedUrls: number;
    successfulScrapes: number;
    failedScrapes: number;
    totalWordsScraped: number;
    avgWordsPerSource: number;
    scrapingTimeMs: number;
  };
} | null>(null);

// ✅ PERFORMANCE FIX: Cache rendered questions to avoid re-rendering LaTeX
const renderedQuestionsCache = ref<
  Array<{
    question: string;
    options: string[];
    explanation: string;
  }>
>([]);

// ✅ PERFORMANCE FIX: LaTeX cache to avoid re-rendering same formulas
const latexCache = new Map<string, string>();

// ✅ PERFORMANCE: Track if questions are being rendered
const isRenderingQuestions = ref(false);

// Computed - Phase 1 readiness check
const isPhase1Complete = computed(() => {
  return currentStep.value === "ready" && contentSafetyPassed.value;
});

// ✅ PERFORMANCE FIX: Pre-render questions when they're loaded (but smarter)
watch(
  testQuestions,
  (newQuestions) => {
    if (newQuestions.length > 0) {
      isRenderingQuestions.value = true;
      logDebug("Pre-rendering questions with LaTeX (optimized)");

      // ⚡ Render in small batches to avoid blocking the UI
      const batchSize = 3; // Render 3 questions at a time
      let currentBatch = 0;

      const renderBatch = () => {
        const startIdx = currentBatch * batchSize;
        const endIdx = Math.min(startIdx + batchSize, newQuestions.length);

        // Render current batch
        for (let i = startIdx; i < endIdx; i++) {
          const q = newQuestions[i];
          renderedQuestionsCache.value[i] = {
            question: renderLatexCached(q.question),
            options: q.options.map((opt) => renderLatexCached(opt)),
            explanation: renderLatexCached(q.explanation),
          };
        }

        currentBatch++;

        // If more batches remain, schedule next batch
        if (endIdx < newQuestions.length) {
          requestAnimationFrame(renderBatch);
        } else {
          isRenderingQuestions.value = false;
          logSuccess("All questions pre-rendered", { count: newQuestions.length });
        }
      };

      // Start rendering first batch immediately
      renderBatch();
    }
  },
  { immediate: true }
);

// ✅ PERFORMANCE FIX: Get current rendered question (cached with memoization)
let lastQuestionIndex = -1;
let lastRenderedQuestion: any = null;

const currentRenderedQuestion = computed(() => {
  if (renderedQuestionsCache.value.length === 0) return null;

  // Return cached result if same question
  if (currentQuestion.value === lastQuestionIndex && lastRenderedQuestion) {
    return lastRenderedQuestion;
  }

  lastQuestionIndex = currentQuestion.value;
  lastRenderedQuestion = renderedQuestionsCache.value[currentQuestion.value];
  return lastRenderedQuestion;
});

// Watch for modal open/close
let modalOpenTimeoutId: number | null = null;

watch(
  () => props.isOpen,
  (newVal) => {
    if (modalOpenTimeoutId) {
      clearTimeout(modalOpenTimeoutId);
    }

    if (newVal) {
      // Debounce modal opening by 50ms to batch operations
      modalOpenTimeoutId = window.setTimeout(() => {
        checkUserType(); // ✅ Check if user is validator when modal opens
        setupMandatoryBlocking();
        resetModal();
        // Store in localStorage for persistence
        savePendingPreTest();
      }, 50);
    } else {
      cleanupMandatoryBlocking();
    }
  }
);

// Save pending pre-test to localStorage
function savePendingPreTest() {
  try {
    const pendingData = {
      sessionId: props.sessionId,
      sessionSubject: props.sessionSubject,
      timestamp: Date.now(),
    };
    localStorage.setItem(PRETEST_STORAGE_KEY, JSON.stringify(pendingData));
    logDebug("Saved pending pre-test to localStorage", pendingData);
  } catch (error) {
    logError("Failed to save pending pre-test", error);
  }
}

// Clear pending pre-test from localStorage
function clearPendingPreTest() {
  try {
    localStorage.removeItem(PRETEST_STORAGE_KEY);
    logDebug("Cleared pending pre-test from localStorage");
  } catch (error) {
    logError("Failed to clear pending pre-test", error);
  }
}

// Check if pre-test already completed in Firestore
async function checkPreTestCompleted(sessionId: string): Promise<boolean> {
  try {
    const preTestRef = doc(db, "preTests", sessionId);
    const preTestDoc = await getDoc(preTestRef);
    const isCompleted = preTestDoc.exists();

    if (isCompleted) {
      logInfo("Pre-test already completed for session", { sessionId });
    }

    return isCompleted;
  } catch (error) {
    logError("Error checking pre-test completion", error);
    return false;
  }
}

// ========================================
// PDF.js WORKER CONFIGURATION (CRITICAL - MUST BE BEFORE EXTRACTION)
// ========================================

// Configure PDF.js worker - use static file from public directory (matches AINoteProcessor)
const setupPDFWorker = () => {
  try {
    // Check if we're in development or production
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? window.location.origin : window.location.origin;

    // Use the static worker file from public directory with absolute URL
    const workerUrl = `${baseUrl}/pdf.worker.min.js`;
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    logDebug("PDF worker setup: Using static worker", { workerUrl });
  } catch (error) {
    logWarn("PDF worker setup: Static file failed, trying CDN fallback", error);
    try {
      // Fallback to unpkg CDN (will work if CSP allows it)
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;
      logDebug("PDF worker setup: Using unpkg CDN");
    } catch (cdnError) {
      logError("PDF worker setup: All methods failed", cdnError);
      // Last resort: try to use local bundled worker
      try {
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/build/pdf.worker.min.js",
          import.meta.url
        ).toString();
        logDebug("PDF worker setup: Using bundled worker");
      } catch (bundledError) {
        logError("PDF worker setup: Bundled worker failed", bundledError);
      }
    }
  }
};

// Initialize PDF worker immediately when modal loads
setupPDFWorker();

// Reset PDF.js worker state function
const resetPDFWorker = () => {
  try {
    // Re-setup the worker to ensure clean state
    setupPDFWorker();
    logDebug("PDF worker reset successfully");
  } catch (error) {
    logWarn("PDF worker reset warning:", error);
  }
};

// ========================================
// PHASE 1: CONTENT EXTRACTION & VALIDATION
// ========================================

/**
 * Extract text from uploaded PDF file
 * Uses same setup as AINoteProcessor (CSP-compliant)
 */
async function extractPDFText(file: File): Promise<string> {
  logDebug("Starting PDF text extraction", { filename: file.name, size: file.size });

  try {
    // Ensure PDF worker is set up before extraction
    setupPDFWorker();

    // Try to read the file - if this fails, it means the File object is no longer accessible
    let arrayBuffer: ArrayBuffer;
    try {
      arrayBuffer = await file.arrayBuffer();
    } catch (readError) {
      logError("Cannot read File object - it may have been revoked or lost", {
        filename: file.name,
        error: readError,
      });
      throw new Error(
        `The uploaded file "${file.name}" is no longer accessible. This can happen if the file was selected a while ago. Please try uploading the file again.`
      );
    }

    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      verbosity: 0, // Suppress console warnings
      isEvalSupported: false, // Security: disable eval
      useSystemFonts: true,
      disableFontFace: false,
      useWorkerFetch: true,
      disableAutoFetch: false,
      disableStream: false,
    });

    const pdf = await loadingTask.promise;
    const numPages = pdf.numPages;

    logDebug("PDF loaded successfully", {
      filename: file.name,
      totalPages: numPages,
    });

    let extractedText = "";

    // Extract text from each page
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();

        // Extract text items and join with spaces
        const pageText = textContent.items
          .map((item: any) => {
            if (item.str && item.str.trim().length > 0) {
              return item.str;
            }
            return "";
          })
          .filter((text: string) => text.length > 0)
          .join(" ");

        if (pageText.trim().length > 0) {
          extractedText += pageText + "\n\n";
          logDebug(`✅ Extracted ${pageText.length} characters from page ${pageNum}/${numPages}`);
        } else {
          logWarn(`⚠️ Page ${pageNum}/${numPages} appears to be empty or image-only`);
        }
      } catch (pageError) {
        logWarn(`Failed to extract text from page ${pageNum}/${numPages}`, pageError);
        // Continue with other pages even if one fails
      }
    }

    // Clean up the extracted text
    const cleanedText = extractedText
      .replace(/\s+/g, " ") // Normalize whitespace
      .replace(/\n\s*\n/g, "\n\n") // Remove excessive line breaks
      .trim();

    if (cleanedText.length === 0) {
      logError("PDF appears to be empty or contains only images", {
        filename: file.name,
        pages: numPages,
      });
      throw new Error(
        `PDF appears to be empty or contains only images: ${file.name}. Please ensure the PDF contains readable text.`
      );
    }

    logSuccess("✅ PDF text extraction complete", {
      filename: file.name,
      pages: numPages,
      textLength: cleanedText.length,
      charactersPerPage: Math.round(cleanedText.length / numPages),
    });

    return cleanedText;
  } catch (error: unknown) {
    // Enhanced error handling with specific messages
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (errorMessage.includes("Invalid PDF structure")) {
      logError("PDF appears to be corrupted or invalid", {
        filename: file.name,
        error: errorMessage,
      });
      throw new Error(
        `The PDF file "${file.name}" appears to be corrupted or invalid. Please try re-uploading or use a different file.`
      );
    } else if (errorMessage.includes("password")) {
      logError("PDF is password protected", { filename: file.name });
      throw new Error(
        `The PDF file "${file.name}" is password protected. Please remove the password and try again.`
      );
    } else if (errorMessage.includes("Failed to fetch")) {
      logError("PDF worker failed to load", { filename: file.name, error: errorMessage });
      // Try to reset worker and retry once
      try {
        resetPDFWorker();
        logInfo("Retrying PDF extraction after worker reset...");
        // Recursive retry (only once)
        return await extractPDFText(file);
      } catch (retryError) {
        logError("PDF extraction failed on retry", retryError);
        throw new Error(
          `The PDF processing component could not load. Please refresh the page and try again.`
        );
      }
    } else {
      logError("PDF extraction failed", error);
      throw new Error(`Text extraction failed for PDF file: ${file.name}`);
    }
  }
}

/**
 * Extract text from DOCX file
 */
async function extractDOCXText(file: File): Promise<string> {
  try {
    logInfo("Extracting text from DOCX file", { filename: file.name });
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    logSuccess("DOCX extraction complete", {
      filename: file.name,
      textLength: result.value.length,
    });
    return result.value.trim();
  } catch (error) {
    logError("DOCX extraction failed", error);
    throw new Error(`Text extraction failed for DOCX file: ${file.name}`);
  }
}

/**
 * Extract text from TXT file
 */
async function extractTXTText(file: File): Promise<string> {
  try {
    logInfo("Extracting text from TXT file", { filename: file.name });
    const text = await file.text();
    logSuccess("TXT extraction complete", {
      filename: file.name,
      textLength: text.length,
    });
    return text.trim();
  } catch (error) {
    logError("TXT extraction failed", error);
    throw new Error(`Text extraction failed for TXT file: ${file.name}`);
  }
}

/**
 * Extract content from uploaded materials (first file only)
 * ✅ NEW: Handles image files gracefully (accepts but skips extraction, uses context only)
 */
async function extractMaterialsContent(): Promise<string> {
  if (!props.materials || props.materials.length === 0) {
    logInfo("No materials uploaded, using appointment context only");
    return "";
  }

  try {
    const material = props.materials[0]; // Use only first file
    logInfo("Extracting content from uploaded file", { fileName: material.name });

    // ✅ NEW: Check if file is an image (no OCR available, use context only)
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    const isImageFile = imageExtensions.some((ext) => material.name.toLowerCase().endsWith(ext));

    if (isImageFile) {
      logInfo("Image file detected - skipping extraction (no OCR), will use context only", {
        fileName: material.name,
        fileType: material.type,
      });

      // Show informative message to user
      await Swal.fire({
        icon: "info",
        title: "Image File Detected",
        html: `
          <div class="text-left">
            <p class="mb-3">You uploaded an image file: <strong>${material.name}</strong></p>
            <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
              <p class="text-sm text-gray-700">
                <strong>ℹ️ Note:</strong> This system does not have OCR (text extraction from images).
              </p>
              <p class="text-sm text-gray-700 mt-2">
                We'll generate questions based on your learning goals description instead:
              </p>
              <p class="text-sm font-medium text-blue-800 mt-2 italic">
                "${props.learningGoals || "Your session topic"}"
              </p>
            </div>
            <p class="text-sm text-gray-600">
              For better results, consider uploading PDF, Word, or text files that we can extract text from.
            </p>
          </div>
        `,
        confirmButtonText: "Continue with Description Only",
        confirmButtonColor: "#3B82F6",
      });

      // Return empty string - will use appointment context (subject + learning goals) instead
      extractionMethod.value = "context-only";
      return "";
    }

    // ✅ FIX: Use original File object if available (avoids CORS entirely!)
    let file: File;

    if (material.originalFile) {
      // Use the original File object passed from appointment form
      file = material.originalFile;
      logSuccess("Using original File object (no network request needed)", {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        lastModified: file.lastModified,
      });

      // Log detailed file info for debugging
      logDebug("File object details", {
        hasArrayBuffer: typeof file.arrayBuffer === "function",
        hasSlice: typeof file.slice === "function",
        hasStream: typeof file.stream === "function",
        constructor: file.constructor.name,
      });
    } else {
      // Fallback: Fetch from Firebase Storage (for backward compatibility)
      logWarn("Original File not available, fetching from Firebase Storage");

      const urlMatch = material.url.match(/o\/(.+?)\?/);
      if (!urlMatch) {
        throw new Error("Invalid Firebase Storage URL format");
      }

      const storagePath = decodeURIComponent(urlMatch[1]);
      const { storage } = await import("@/firebase");
      const { ref: storageRef, getBlob } = await import("firebase/storage");

      const fileRef = storageRef(storage, storagePath);
      const blob = await getBlob(fileRef);
      file = new File([blob], material.name, { type: material.type });
    }

    // Extract text based on file type
    let text = "";
    if (material.name.endsWith(".pdf")) {
      extractionMethod.value = "pdf";
      try {
        text = await extractPDFText(file);
        logSuccess("PDF extraction completed successfully", {
          filename: material.name,
          extractedLength: text.length,
        });
      } catch (pdfError) {
        logError("PDF extraction failed - file may be corrupted or password-protected", {
          filename: material.name,
          error: pdfError,
        });

        // Provide more helpful error message
        const errorMsg = pdfError instanceof Error ? pdfError.message : String(pdfError);
        if (errorMsg.includes("no longer accessible") || errorMsg.includes("NotReadableError")) {
          throw new Error(
            `The PDF file "${material.name}" cannot be accessed. The file reference has been lost. Please upload the file again.`
          );
        } else if (errorMsg.includes("password") || errorMsg.includes("encrypted")) {
          throw new Error(
            `The PDF file "${material.name}" is password-protected. Please upload an unprotected PDF file.`
          );
        } else {
          throw new Error(
            `Text extraction failed for "${material.name}". The file is corrupted or uses an unsupported PDF format. Please try a different file.`
          );
        }
      }
    } else if (material.name.endsWith(".docx")) {
      extractionMethod.value = "docx";
      text = await extractDOCXText(file);
    } else if (material.name.endsWith(".txt")) {
      extractionMethod.value = "txt";
      text = await extractTXTText(file);
    } else if (material.name.endsWith(".ppt") || material.name.endsWith(".pptx")) {
      // PowerPoint files - inform user we can't extract text, use context only
      logInfo("PowerPoint file detected - skipping extraction, will use context only", {
        fileName: material.name,
      });

      await Swal.fire({
        icon: "info",
        title: "PowerPoint File Detected",
        html: `
          <div class="text-left">
            <p class="mb-3">You uploaded a PowerPoint file: <strong>${material.name}</strong></p>
            <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
              <p class="text-sm text-gray-700">
                <strong>ℹ️ Note:</strong> PowerPoint extraction is not yet supported.
              </p>
              <p class="text-sm text-gray-700 mt-2">
                We'll generate questions based on your learning goals description instead.
              </p>
            </div>
            <p class="text-sm text-gray-600">
              For better results, consider uploading PDF or Word files.
            </p>
          </div>
        `,
        confirmButtonText: "Continue with Description Only",
        confirmButtonColor: "#3B82F6",
      });

      extractionMethod.value = "context-only";
      return "";
    } else {
      throw new Error(`Unsupported file type: ${material.name}`);
    }

    return text;
  } catch (error) {
    logError("Failed to extract content from materials", error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : "The file could not be read due to an internal error";
    validationErrors.value.push(`Could not read file: ${props.materials[0].name}. ${errorMessage}`);

    // Show user-friendly error with option to exit
    await Swal.fire({
      icon: "error",
      title: "Content Validation Failed",
      html: `
        <div class="text-left">
          <p class="mb-3">Could not read file: <strong>${props.materials[0].name}</strong></p>
          <p class="text-sm text-gray-600 mb-3">${errorMessage}</p>
          <p class="text-sm text-gray-600">Please check your uploaded materials and try again.</p>
        </div>
      `,
      confirmButtonText: "OK",
      allowOutsideClick: true,
    });

    // Allow user to close modal and fix the file
    emit("close");
    throw error;
  }
}
/**
 * Validate content safety (XSS, profanity, academic validation)
 */
async function validateContentSafety(content: string): Promise<boolean> {
  try {
    logInfo("Running content safety checks for pre-test");

    // Import safety utilities
    const { comprehensiveSecurityCheck } = await import("@/utils/contentSafetyFilter");
    const { validateAcademicContent } = await import("@/utils/academicContentValidator");

    // Step 1: Security check (XSS, SQL injection, profanity)
    const securityCheck = comprehensiveSecurityCheck(content);
    if (securityCheck.blocked) {
      validationErrors.value.push(securityCheck.message || "Content contains unsafe material");

      // ✅ FIX: Show user-friendly error with option to go back and fix
      const result = await Swal.fire({
        icon: "error",
        title: "Content Validation Failed",
        html: `
          <div class="text-left">
            <p class="mb-3"><strong>${
              securityCheck.message || "Content contains unsafe material"
            }</strong></p>
            <div class="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3">
              <p class="text-sm text-gray-700 mb-2">
                ${
                  securityCheck.reason ||
                  "The uploaded content did not pass our security validation."
                }
              </p>
              ${
                securityCheck.detectedTerms && securityCheck.detectedTerms.length > 0
                  ? `<p class="text-xs text-gray-600 mt-2"><strong>Note:</strong> If this is legitimate educational content (like math formulas), please try re-uploading or use different materials.</p>`
                  : ""
              }
            </div>
            <p class="text-sm text-gray-600">You can:</p>
            <ul class="text-sm text-gray-600 list-disc list-inside mt-2 space-y-1">
              <li><strong>Go Back:</strong> Edit your learning goals or upload different materials</li>
              <li><strong>Cancel:</strong> Exit and start over</li>
            </ul>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Go Back & Edit",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3B82F6",
        cancelButtonColor: "#6B7280",
        allowOutsideClick: false,
      });

      if (result.isConfirmed) {
        // User wants to go back and edit - emit close event
        emit("close");
      }

      return false;
    }

    // Step 2: Readability check (matches PostTest - minimum 50% readable content)
    // Only check if content is from uploaded file (not appointment context)
    if (props.materials && props.materials.length > 0 && content.length > 100) {
      const words = content.split(/\s+/).filter((word) => word.length > 0);
      const meaningfulWords = words.filter((word) => {
        const cleanWord = word.replace(/^[^\w]+|[^\w]+$/g, "");
        return (
          cleanWord.length >= 1 &&
          (/^[a-zA-Z][a-zA-Z'-]*[a-zA-Z]?$/.test(cleanWord) || /^\d+$/.test(cleanWord))
        );
      });
      const readabilityPercentage =
        words.length > 0 ? Math.round((meaningfulWords.length / words.length) * 100) : 0;

      logDebug("Readability analysis for pre-test", {
        totalWords: words.length,
        meaningfulWords: meaningfulWords.length,
        readabilityPercentage,
      });

      if (readabilityPercentage < 50) {
        logWarn("File content has low readability", { readabilityPercentage });

        await Swal.fire({
          icon: "warning",
          title: "Low Content Quality",
          html: `
            <div class="text-left">
              <p class="mb-3">Your uploaded material appears to have low readability.</p>
              <div class="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-3">
                <p class="mb-2"><strong>Readability Score:</strong> <span class="text-orange-700">${readabilityPercentage}%</span></p>
                <p><strong>Minimum Required:</strong> <span class="text-green-700">50%</span></p>
              </div>
              <p class="text-sm text-gray-600 mt-3">The content may contain too many symbols, gibberish, or unreadable text. Please use a file with clearer educational content.</p>
            </div>
          `,
          confirmButtonText: "OK",
        });

        validationErrors.value.push(
          `Content readability too low (${readabilityPercentage}% - minimum 50% required)`
        );
        return false;
      }

      logSuccess("Readability check passed", { readabilityPercentage });
    }

    // Step 3: Academic content validation
    // ONLY validate if there are uploaded materials - skip for appointment context only
    if (props.materials && props.materials.length > 0) {
      const academicCheck = await validateAcademicContent(content, props.sessionSubject);
      if (!academicCheck.isValid) {
        validationErrors.value.push("Content is not valid academic material");
        return false;
      }
      logSuccess("Academic content validation passed");
    } else {
      logInfo(
        "Skipping academic validation - using appointment context only (subject + learning goals)"
      );
    }

    logSuccess("Content safety validation passed");
    contentSafetyPassed.value = true;
    return true;
  } catch (error) {
    logError("Content safety validation error", error);
    validationErrors.value.push(
      "The content safety check failed. Please ensure your file contains appropriate educational material and is not corrupted."
    );
    return false;
  }
}

/**
 * Detect if text is gibberish/meaningless
 * Returns true if text appears to be random characters or nonsensical
 */
function isGibberishText(text: string): boolean {
  if (!text || text.trim().length < 10) return true;

  const cleanText = text.trim().toLowerCase();

  // Check 1: High ratio of non-alphanumeric characters (repeated symbols)
  const nonAlphanumeric = (cleanText.match(/[^a-z0-9\s]/g) || []).length;
  const alphanumeric = (cleanText.match(/[a-z0-9]/g) || []).length;
  if (alphanumeric > 0 && nonAlphanumeric / alphanumeric > 0.5) return true;

  // Check 2: Very few vowels (English/Filipino both need vowels)
  const vowels = (cleanText.match(/[aeiou]/g) || []).length;
  const consonants = (cleanText.match(/[bcdfghjklmnpqrstvwxyz]/g) || []).length;
  if (consonants > 5 && vowels / consonants < 0.2) return true;

  // Check 3: Check for repeated characters (e.g., "aaaaaaa", "jjjjjj")
  const repeatedChars = /([a-z])\1{4,}/i.test(cleanText);
  if (repeatedChars) return true;

  // Check 4: No recognizable words (check for common English/Filipino words)
  const commonWords = [
    // English
    "the",
    "is",
    "are",
    "was",
    "were",
    "have",
    "has",
    "had",
    "do",
    "does",
    "will",
    "can",
    "could",
    "would",
    "should",
    "about",
    "what",
    "when",
    "where",
    "who",
    "why",
    "how",
    "and",
    "or",
    "but",
    "if",
    "then",
    "for",
    "from",
    "to",
    "with",
    "of",
    "in",
    "on",
    "at",
    "by",
    "learn",
    "study",
    "understand",
    "know",
    // Filipino
    "ang",
    "ng",
    "mga",
    "sa",
    "ay",
    "na",
    "at",
    "para",
    "kung",
    "ako",
    "siya",
    "tayo",
    "kami",
    "nila",
    "kayo",
    "mo",
    "ko",
    "niya",
    "natin",
  ];
  const hasCommonWord = commonWords.some((word) => cleanText.includes(word));
  const words = cleanText.split(/\s+/);

  // If text is longer than 20 chars and has no common words, likely gibberish
  if (cleanText.length > 20 && !hasCommonWord && words.length > 2) return true;

  // Check 5: Random keyboard patterns (asdf, jkl;, qwerty, etc.)
  const keyboardPatterns = [
    "asdf",
    "jkl",
    "qwer",
    "zxcv",
    "hjkl",
    "uiop",
    "bnm",
    "asd",
    "jkl",
    "qwe",
    "zxc",
    "hjk",
    "uio",
    "bnm",
  ];
  const hasKeyboardPattern = keyboardPatterns.some((pattern) => cleanText.includes(pattern));
  if (hasKeyboardPattern) return true;

  return false;
}

/**
 * Detect content language using HuggingFace backend
 */
async function detectContentLanguage(content: string): Promise<void> {
  try {
    logInfo("Detecting content language with HuggingFace");

    const { backendAIService } = await import("@/services/backendAIService");

    // Analyze content (language only, skip subject since we have it from appointment)
    const hfAnalysis = await backendAIService.analyzeContent(
      content.substring(0, 2000), // First 2000 chars for efficiency
      true // Skip subject detection
    );

    // Map HF result to content language
    if (hfAnalysis.language === "English" || hfAnalysis.language === "english") {
      contentLanguage.value = "english";
    } else if (hfAnalysis.language === "Filipino" || hfAnalysis.language === "filipino") {
      contentLanguage.value = "filipino";
    } else if (hfAnalysis.language === "Taglish" || hfAnalysis.language === "taglish") {
      contentLanguage.value = "mixed";
    } else {
      contentLanguage.value = "unknown";
    }

    // Store language confidence
    languageConfidence.value = hfAnalysis.languageConfidence;

    logSuccess("Language detected", {
      detected: contentLanguage.value,
      confidence: hfAnalysis.languageConfidence,
    });
  } catch (error) {
    logError("Language detection failed, defaulting to English", error);
    contentLanguage.value = "english"; // Fallback
  }
}

/**
 * Execute Phase 1 pipeline: Extract → Validate → Detect Language
 * Stops before AI generation (Phase 2)
 */
async function executePhase1Pipeline(): Promise<boolean> {
  isGenerating.value = true;
  validationErrors.value = [];

  // Track Phase 1 start time
  phase1StartTime.value = new Date().toISOString();

  try {
    // ========================================
    // STEP 1/13: EXTRACT CONTENT FROM MATERIALS
    // ========================================
    currentStep.value = "extracting";
    currentAIStep.value = 1;
    currentAIStepMessage.value = "📄 Extracting content from uploaded materials...";
    aiProgress.value = 8;

    logInfo("🔍 Step 1/13: Extracting content from materials");

    extractedContent.value = await extractMaterialsContent();

    // If no materials, use just subject + learning goals as context
    if (!extractedContent.value) {
      extractedContent.value = `Subject: ${props.sessionSubject}\nLearning Goals: ${
        props.learningGoals || "General understanding"
      }`;
      logInfo("Step 1/13: No materials uploaded, using appointment context only");
    }

    logSuccess("✅ Step 1/13: Content extraction complete", {
      contentLength: extractedContent.value.length,
      hasMaterials: props.materials && props.materials.length > 0,
    });

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 2/13: VALIDATE CONTENT SAFETY
    // ========================================
    currentStep.value = "validating";
    currentAIStep.value = 2;
    currentAIStepMessage.value = "🛡️ Validating content safety and academic quality...";
    aiProgress.value = 15;

    logInfo("🔍 Step 2/13: Validating content safety");

    const isSafe = await validateContentSafety(extractedContent.value);
    if (!isSafe) {
      throw new Error("Content safety validation failed");
    }

    logSuccess("✅ Step 2/13: Content safety validation passed");

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 2.5/13: VALIDATE SUBJECT RELEVANCE (CRITICAL!)
    // ========================================
    currentAIStep.value = 2.5;
    currentAIStepMessage.value = "🎯 Validating subject relevance...";
    aiProgress.value = 19;

    logInfo("🔍 Step 2.5/13: Validating subject relevance");

    // Only validate if we have actual extracted content from materials
    // Skip if using only appointment context (subject + learning goals)
    if (props.materials && props.materials.length > 0 && extractedContent.value.length > 200) {
      const relevanceCheck = await geminiService.validateSubjectRelevance(
        extractedContent.value,
        props.sessionSubject
      );

      logDebug("Subject relevance check completed", {
        isRelevant: relevanceCheck.isRelevant,
        relevanceScore: relevanceCheck.relevanceScore,
        detectedSubject: relevanceCheck.detectedSubject,
        expectedSubject: relevanceCheck.expectedSubject,
        confidence: relevanceCheck.confidence,
      });

      // Block if relevance score is too low (below 60%)
      if (!relevanceCheck.isRelevant) {
        logWarn("Content blocked due to subject mismatch", {
          relevanceScore: relevanceCheck.relevanceScore,
          detectedSubject: relevanceCheck.detectedSubject,
          expectedSubject: relevanceCheck.expectedSubject,
        });

        validationErrors.value.push(
          relevanceCheck.message || "Content does not match session subject"
        );

        await Swal.fire({
          icon: "warning",
          title: "Subject Mismatch Detected",
          html: `
            <div class="text-left">
              <p class="mb-3">${relevanceCheck.message}</p>
              <div class="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><strong>Expected Subject:</strong> ${relevanceCheck.expectedSubject}</p>
                <p><strong>Detected Subject:</strong> ${relevanceCheck.detectedSubject}</p>
                <p><strong>Relevance Score:</strong> ${relevanceCheck.relevanceScore}% (minimum: 60%)</p>
                <p><strong>Confidence:</strong> ${relevanceCheck.confidence}</p>
              </div>
              <p class="mt-3 text-sm text-gray-600">Please upload materials that are relevant to <strong>${props.sessionSubject}</strong> to ensure accurate pre-test generation.</p>
            </div>
          `,
          confirmButtonText: "Upload Different Materials",
          confirmButtonColor: "#EF4444",
        });

        // ✅ FIX: Emit close event to let user go back and upload different files
        logInfo("User acknowledged subject mismatch - closing modal to allow re-upload");
        emit("close");
        // ✅ FIX: Mark as user cancellation (not an error) to prevent generic error popup
        const userCancellationError = new Error("Subject relevance validation failed");
        userCancellationError.name = "UserCancellation";
        throw userCancellationError;
      }

      logSuccess("✅ Step 2.5/13: Subject relevance validated successfully", {
        relevanceScore: relevanceCheck.relevanceScore,
        detectedSubject: relevanceCheck.detectedSubject,
      });
    } else {
      logInfo("Step 2.5/13: Skipping material subject relevance check (no materials uploaded)");

      // ✅ NEW: Validate learning goals against session subject (check for gibberish first)
      if (props.learningGoals && props.learningGoals.trim().length > 10) {
        logInfo("🔍 Step 2.5B/13: Validating learning goals against session subject");

        // Check for gibberish FIRST before making API call
        const isGibberish = isGibberishText(props.learningGoals);

        if (isGibberish) {
          logWarn("Learning goals detected as gibberish/irrelevant input", {
            input: props.learningGoals.substring(0, 100),
          });

          const result = await Swal.fire({
            icon: "warning",
            title: "Input Doesn't Match Subject",
            html: `
              <div class="text-left">
                <p class="mb-3">The learning goals you entered don't appear to be related to <strong>${
                  props.sessionSubject
                }</strong>.</p>
                
                <div class="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3">
                  <p class="text-sm"><strong>⚠️ Your Input:</strong></p>
                  <p class="text-sm font-mono text-gray-700 mt-1">"${props.learningGoals.substring(
                    0,
                    100
                  )}${props.learningGoals.length > 100 ? "..." : ""}"</p>
                </div>

                <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
                  <p class="text-sm font-semibold text-blue-900 mb-2">📝 What happens next:</p>
                  <p class="text-sm text-blue-800">
                    If you proceed, we'll generate <strong>general ${
                      props.sessionSubject
                    } questions</strong> 
                    to assess your foundational knowledge instead.
                  </p>
                </div>

                <p class="text-sm text-gray-600">
                  <strong>Tip:</strong> For personalized questions, please describe specific topics you want to learn 
                  (e.g., "photosynthesis", "climate change", "plate tectonics").
                </p>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Proceed to Pre-Test",
            cancelButtonText: "Go Back & Edit",
            confirmButtonColor: "#3B82F6",
            cancelButtonColor: "#6B7280",
            allowOutsideClick: false,
          });

          if (result.isDismissed || result.dismiss === Swal.DismissReason.cancel) {
            logInfo("User chose to go back and edit learning goals");
            emit("close");
            const userCancellationError = new Error(
              "User chose to edit learning goals (gibberish detected)"
            );
            userCancellationError.name = "UserCancellation";
            throw userCancellationError;
          }

          logInfo("User chose to proceed with general questions despite gibberish input");
          // ✅ Clear learning goals to force general questions
          extractedContent.value = `Subject: ${props.sessionSubject}\nGenerate general foundational questions.`;
        } else {
          // Not gibberish, check relevance to subject
          const goalsRelevanceCheck = await geminiService.validateSubjectRelevance(
            `User wants to learn: ${props.learningGoals}`,
            props.sessionSubject
          );

          logDebug("Learning goals relevance check completed", {
            isRelevant: goalsRelevanceCheck.isRelevant,
            relevanceScore: goalsRelevanceCheck.relevanceScore,
            detectedSubject: goalsRelevanceCheck.detectedSubject,
            expectedSubject: goalsRelevanceCheck.expectedSubject,
          });

          // ⚠️ WARN if learning goals don't match subject (below 50% relevance)
          if (!goalsRelevanceCheck.isRelevant && goalsRelevanceCheck.relevanceScore < 50) {
            logWarn("Learning goals don't match session subject", {
              relevanceScore: goalsRelevanceCheck.relevanceScore,
              detectedSubject: goalsRelevanceCheck.detectedSubject,
              expectedSubject: goalsRelevanceCheck.expectedSubject,
            });

            const result = await Swal.fire({
              icon: "warning",
              title: "Input Doesn't Match Subject",
              html: `
                <div class="text-left">
                  <p class="mb-3">Your learning goals appear to be about <strong>"${
                    goalsRelevanceCheck.detectedSubject
                  }"</strong>, but this session is for <strong>"${
                props.sessionSubject
              }"</strong>.</p>
                  
                  <div class="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-3">
                    <p class="text-sm"><strong>Your Input:</strong></p>
                    <p class="text-sm italic text-gray-700 mt-1">"${props.learningGoals.substring(
                      0,
                      120
                    )}${props.learningGoals.length > 120 ? "..." : ""}"</p>
                    <p class="text-sm font-medium text-amber-700 mt-2">Match Score: ${
                      goalsRelevanceCheck.relevanceScore
                    }%</p>
                  </div>

                  <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
                    <p class="text-sm font-semibold text-blue-900 mb-2">📝 What happens next:</p>
                    <p class="text-sm text-blue-800">
                      If you proceed, we'll generate <strong>general ${
                        props.sessionSubject
                      } questions</strong> 
                      instead of personalized questions based on your input.
                    </p>
                  </div>

                  <p class="text-sm text-gray-600">
                    <strong>Tip:</strong> Make sure your learning goals match the session subject for best results.
                  </p>
                </div>
              `,
              showCancelButton: true,
              confirmButtonText: "Proceed to Pre-Test",
              cancelButtonText: "Go Back & Edit",
              confirmButtonColor: "#3B82F6",
              cancelButtonColor: "#6B7280",
              allowOutsideClick: false,
            });

            if (result.isDismissed || result.dismiss === Swal.DismissReason.cancel) {
              logInfo("User chose to go back and edit learning goals");
              emit("close");
              const userCancellationError = new Error(
                "User chose to edit learning goals due to subject mismatch"
              );
              userCancellationError.name = "UserCancellation";
              throw userCancellationError;
            }

            logInfo("User chose to proceed with general questions despite subject mismatch");
            // ✅ Clear learning goals to force general questions
            extractedContent.value = `Subject: ${props.sessionSubject}\nGenerate general foundational questions.`;
          }
        }
      }
    }

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 2.75/13: VALIDATE CONTEXT-MATERIAL RELEVANCE (NEW!)
    // ========================================
    currentAIStep.value = 2.75;
    currentAIStepMessage.value = "🔍 Checking if materials match your learning goals...";
    aiProgress.value = 21;

    logInfo("🔍 Step 2.75/13: Validating context-material relevance");

    // Only validate if we have BOTH materials AND learning goals/context
    // Skip if using only appointment context OR if materials couldn't be extracted (images, PPT, etc.)
    if (
      props.materials &&
      props.materials.length > 0 &&
      props.learningGoals &&
      props.learningGoals.trim().length > 10 &&
      extractedContent.value.length > 100 &&
      extractionMethod.value !== "context-only"
    ) {
      const contextMaterialCheck = await geminiService.validateContextMaterialRelevance(
        props.learningGoals,
        extractedContent.value,
        props.sessionSubject
      );

      logDebug("Context-material relevance check completed", {
        isRelevant: contextMaterialCheck.isRelevant,
        matchScore: contextMaterialCheck.matchScore,
        userRequestedTopic: contextMaterialCheck.userRequestedTopic,
        detectedMaterialTopic: contextMaterialCheck.detectedMaterialTopic,
        confidence: contextMaterialCheck.confidence,
      });

      // ✅ WARN user if mismatch detected, but allow them to continue
      if (!contextMaterialCheck.isRelevant) {
        logWarn("Context-material mismatch detected", {
          matchScore: contextMaterialCheck.matchScore,
          userRequestedTopic: contextMaterialCheck.userRequestedTopic,
          detectedMaterialTopic: contextMaterialCheck.detectedMaterialTopic,
        });

        const result = await Swal.fire({
          icon: "warning",
          title: "Material Mismatch Detected",
          html: `
            <div class="text-left">
              <div class="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-3">
                <p class="mb-2"><strong>What you want to learn:</strong></p>
                <p class="text-sm italic text-gray-700 mb-3">"${contextMaterialCheck.userRequestedTopic}"</p>
                
                <p class="mb-2"><strong>What your uploaded materials contain:</strong></p>
                <p class="text-sm italic text-gray-700">"${contextMaterialCheck.detectedMaterialTopic}"</p>
              </div>
              
              <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
                <p class="text-sm font-medium text-blue-800 mb-2">Match Score: ${contextMaterialCheck.matchScore}%</p>
                <p class="text-sm text-gray-700">
                  ${contextMaterialCheck.message}
                </p>
              </div>

              <p class="text-sm text-gray-600 mt-3">
                <strong>Options:</strong>
              </p>
              <ul class="text-sm text-gray-600 list-disc list-inside mt-2 space-y-1">
                <li><strong>Upload Different Materials:</strong> Close this modal and upload files that match your learning goals</li>
                <li><strong>Continue Anyway:</strong> We'll focus on your description and generate questions based on what you want to learn</li>
              </ul>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Continue Anyway",
          cancelButtonText: "Upload Different Materials",
          confirmButtonColor: "#3B82F6",
          cancelButtonColor: "#EF4444",
          allowOutsideClick: false,
        });

        if (result.isDismissed || result.dismiss === Swal.DismissReason.cancel) {
          // User wants to upload different materials
          logInfo("User chose to upload different materials");
          emit("close");
          // ✅ FIX: Mark as user cancellation (not an error) to prevent generic error popup
          const userCancellationError = new Error("User chose to upload different materials");
          userCancellationError.name = "UserCancellation";
          throw userCancellationError;
        }

        // User chose to continue - use context only, ignore materials
        logInfo("User chose to continue despite mismatch - will prioritize context over materials");

        // Clear extracted content and rely on context only
        extractedContent.value = `Subject: ${props.sessionSubject}\nLearning Goals: ${props.learningGoals}`;
        extractionMethod.value = "context-only";
      } else {
        logSuccess("✅ Step 2.75/13: Context-material match validated successfully", {
          matchScore: contextMaterialCheck.matchScore,
          userRequestedTopic: contextMaterialCheck.userRequestedTopic,
          detectedMaterialTopic: contextMaterialCheck.detectedMaterialTopic,
        });
      }
    } else {
      logInfo(
        "Step 2.75/13: Skipping context-material validation (no materials or context, or using images/PPT)"
      );
    }

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 3/13: DETECT LANGUAGE WITH HUGGINGFACE
    // ========================================
    currentStep.value = "language-detection";
    currentAIStep.value = 3;
    currentAIStepMessage.value = "🌐 Detecting content language with HuggingFace...";
    aiProgress.value = 23;

    logInfo("🔍 Step 3/13: Detecting content language with HuggingFace");

    await detectContentLanguage(extractedContent.value);

    logSuccess("✅ Step 3/13: Language detection complete");

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 4/13: DETERMINE OUTPUT LANGUAGE BASED ON SUBJECT (PRE-TEST ONLY)
    // ========================================
    currentAIStep.value = 4;
    currentAIStepMessage.value = "🌐 Determining output language based on subject...";
    aiProgress.value = 31;

    logInfo("🔍 Step 4/13: Determining output language based on subject (Pre-Test)");

    // ✅ PRE-TEST LANGUAGE RULE: Based on subject, not content
    // English subjects: Output in English
    const englishSubjects = [
      "oral communication",
      "reading and writing",
      "21st century literature from the philippines and the world",
      "general mathematics",
      "statistics and probability",
      "earth and life science",
      "physical science",
      "earth science",
    ];

    // Filipino subjects: Output in Filipino
    const filipinoSubjects = [
      "komunikasyon at pananaliksik sa wika at kulturang pilipino",
      "pagbasa at pagsusuri ng iba't-ibang teksto tungo sa pananaliksik",
    ];

    const subjectLower = props.sessionSubject.toLowerCase().trim();

    if (englishSubjects.some((subject) => subjectLower.includes(subject))) {
      contentLanguage.value = "english";
      logSuccess("✅ Step 4/13: Pre-Test output language set to ENGLISH based on subject", {
        subject: props.sessionSubject,
        rule: "English subjects → English output",
      });
    } else if (filipinoSubjects.some((subject) => subjectLower.includes(subject))) {
      contentLanguage.value = "filipino";
      logSuccess("✅ Step 4/13: Pre-Test output language set to FILIPINO based on subject", {
        subject: props.sessionSubject,
        rule: "Filipino subjects → Filipino output",
      });
    } else {
      // Fallback: Use HuggingFace detection for subjects not in the list
      logInfo("Subject not in predefined list, using HuggingFace detection as fallback", {
        subject: props.sessionSubject,
      });
      // contentLanguage already set from Step 3 (HuggingFace detection)
    }

    logSuccess("✅ Step 4/13: Output language determined", {
      subject: props.sessionSubject,
      outputLanguage: contentLanguage.value,
      note: "No user prompt - automatically determined by subject",
    });

    // Phase 1 complete - ready for Phase 2 (AI generation)
    currentStep.value = "ready";

    // Track Phase 1 end time
    phase1EndTime.value = new Date().toISOString();

    logSuccess("✅ PHASE 1 COMPLETE - Content validated and ready for AI generation", {
      contentLength: extractedContent.value.length,
      language: contentLanguage.value,
      safetyPassed: contentSafetyPassed.value,
      duration: `${
        new Date(phase1EndTime.value).getTime() - new Date(phase1StartTime.value).getTime()
      }ms`,
    });

    return true;
  } catch (error) {
    logError("Phase 1 pipeline failed", error);
    currentStep.value = "intro"; // Reset

    // ✅ FIX: Check if this is a user cancellation (not a real error)
    const isUserCancellation = error instanceof Error && error.name === "UserCancellation";

    if (isUserCancellation) {
      // User intentionally chose to go back - don't show error popup
      logDebug("User cancellation detected - skipping error popup");
      return false;
    }

    // Check for authentication errors
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isAuthError =
      errorMessage.includes("401") ||
      errorMessage.includes("Unauthorized") ||
      errorMessage.includes("not authenticated");

    if (isAuthError) {
      await Swal.fire({
        icon: "error",
        title: "Authentication Error",
        html: `
          <p>Your session has expired. Please refresh the page and try again.</p>
          <p class="mt-2 text-sm text-gray-600">If the problem persists, try logging out and back in.</p>
        `,
        confirmButtonText: "Refresh Page",
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.reload();
        }
      });
    } else {
      // Don't show error popup here if it was already shown in other validation steps
      // (extractMaterialsContent, validateContentSafety, etc.)
      const alreadyShownErrors = [
        "Could not read file",
        "Content contains unsafe material",
        "Subject relevance validation failed",
        "User chose to upload different materials",
        "User chose to edit learning goals",
      ];

      const errorAlreadyShown = alreadyShownErrors.some((err) => errorMessage.includes(err));

      if (!errorAlreadyShown) {
        // Generic error that wasn't already shown - offer to go back and edit
        const result = await Swal.fire({
          icon: "error",
          title: "Content Validation Failed",
          html: `
            <div class="text-left">
              <p class="mb-3">${
                validationErrors.value.length > 0
                  ? validationErrors.value.join("<br>")
                  : "The uploaded file could not pass our academic content validation checks. This may be due to: (1) File corruption or damage during upload, (2) Unreadable text, (3) Content that doesn't match educational standards, or (4) Missing or incomplete file content."
              }</p>
              <p class="text-sm text-gray-600 mb-3">You can:</p>
              <ul class="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li><strong>Go Back:</strong> Edit your learning goals or upload different materials</li>
                <li><strong>Cancel:</strong> Exit and start over</li>
              </ul>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Go Back & Edit",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3B82F6",
          cancelButtonColor: "#6B7280",
          allowOutsideClick: false,
        });

        if (result.isConfirmed) {
          // User wants to go back and edit - emit close event
          emit("close");
        }
      }
    }
    return false;
  } finally {
    isGenerating.value = false;
  }
}

// ========================================
// METHODS
// ========================================

function resetModal() {
  currentStep.value = "intro";
  currentQuestion.value = 0;
  answers.value = [];
  extractedContent.value = "";
  contentLanguage.value = "unknown";
  contentSafetyPassed.value = false;
  validationErrors.value = [];
  isNavigating.value = false; // ✅ Reset navigation state
}

async function startAssessment() {
  // Execute Phase 1 pipeline
  const success = await executePhase1Pipeline();

  if (success) {
    // Phase 1 complete - proceed to Phase 2 (AI generation)
    logInfo("Phase 1 complete, starting Phase 2: AI question generation");
    await generateAIQuestions();
  }
}

// ========================================
// PHASE 2: AI QUESTION GENERATION
// ========================================

/**
 * QUALITY VALIDATION: 7 Standards for Pre-Test Questions
 * Based on Philippine K-12 curriculum and educational best practices
 */
interface QualityValidationResult {
  passed: boolean;
  score: number; // 0-100
  passedStandards: string[];
  failures: string[];
}

function validateQuestionQuality(
  questions: Array<{
    question: string;
    options: string[];
    correctAnswer: number;
    explanation: string;
  }>,
  subject: string
): QualityValidationResult {
  const standards = {
    appropriateDifficulty: false,
    noTrickQuestions: false,
    philippineCurriculum: false,
    clearConcise: false,
    bilingualSupport: false,
    cognitiveDistribution: false,
    factualAccuracy: false,
  };

  const failures: string[] = [];
  let totalScore = 0;

  // Standard 1: Appropriate Difficulty (foundational/basic level)
  const hasComplexTerms = questions.some((q) => {
    const complexWords = [
      "synthesize",
      "critique",
      "hypothesize",
      "extrapolate",
      "deconstruct",
      "paradigm",
    ];
    return complexWords.some((word) => q.question.toLowerCase().includes(word));
  });

  if (!hasComplexTerms && questions.length === 10) {
    standards.appropriateDifficulty = true;
    totalScore += 15;
  } else {
    failures.push("Questions may be too advanced for baseline assessment");
  }

  // Standard 2: No Trick Questions (clear, unambiguous)
  const hasTrickQuestions = questions.some((q) => {
    return (
      q.question.includes("NOT") ||
      q.question.includes("EXCEPT") ||
      q.question.includes("never") ||
      q.options.filter((opt) => opt.toLowerCase().includes("all of the above")).length > 0
    );
  });

  if (!hasTrickQuestions) {
    standards.noTrickQuestions = true;
    totalScore += 15;
  } else {
    failures.push("Avoid trick questions with negations or 'all of the above' options");
  }

  // Standard 3: Philippine Curriculum Alignment (Grades 11-12)
  const philippineTopics = [
    "filipino",
    "pilipino",
    "panitikan",
    "wika",
    "komunikasyon",
    "pagbasa",
    "pagsusuri",
  ];
  const hasPhilippineContext =
    philippineTopics.some((topic) => subject.toLowerCase().includes(topic)) ||
    questions.some((q) =>
      philippineTopics.some((topic) => q.question.toLowerCase().includes(topic))
    );

  // Always pass if English/Math/Science (universal subjects)
  const universalSubjects = [
    "mathematics",
    "science",
    "english",
    "physics",
    "chemistry",
    "biology",
  ];
  if (universalSubjects.some((s) => subject.toLowerCase().includes(s)) || hasPhilippineContext) {
    standards.philippineCurriculum = true;
    totalScore += 15;
  } else {
    failures.push("Questions should align with Philippine K-12 curriculum");
  }

  // Standard 4: Clear & Concise (readable length, no overly complex phrasing)
  const averageQuestionLength =
    questions.reduce((sum, q) => sum + q.question.length, 0) / questions.length;

  if (averageQuestionLength < 200 && averageQuestionLength > 30) {
    standards.clearConcise = true;
    totalScore += 15;
  } else {
    failures.push("Questions should be clear and concise (30-200 characters optimal)");
  }

  // Standard 5: Bilingual Support (language consistency)
  const languageConsistent = questions.every((q) => {
    const hasEnglish = /[a-zA-Z]{3,}/.test(q.question);
    const hasFilipino = /\b(ang|mga|ng|sa|ay|at|para|hindi)\b/i.test(q.question);

    // Questions should be consistently in one language or clearly mixed (Taglish)
    return hasEnglish || hasFilipino;
  });

  if (languageConsistent) {
    standards.bilingualSupport = true;
    totalScore += 10;
  } else {
    failures.push("Language consistency issue detected");
  }

  // Standard 6: Cognitive Distribution (30% recall, 40% application, 30% analysis)
  const recallKeywords = ["what is", "define", "identify", "which", "who", "when"];
  const applicationKeywords = ["apply", "calculate", "solve", "use", "demonstrate", "how"];
  const analysisKeywords = ["why", "explain", "compare", "analyze", "relate", "predict"];

  let recallCount = 0;
  let applicationCount = 0;
  let analysisCount = 0;

  questions.forEach((q) => {
    const qLower = q.question.toLowerCase();
    if (recallKeywords.some((kw) => qLower.includes(kw))) recallCount++;
    else if (applicationKeywords.some((kw) => qLower.includes(kw))) applicationCount++;
    else if (analysisKeywords.some((kw) => qLower.includes(kw))) analysisCount++;
  });

  const total = questions.length;
  const recallPct = (recallCount / total) * 100;
  const applicationPct = (applicationCount / total) * 100;
  const analysisPct = (analysisCount / total) * 100;

  // Allow ±10% flexibility on distribution
  if (
    recallPct >= 20 &&
    recallPct <= 40 &&
    applicationPct >= 30 &&
    applicationPct <= 50 &&
    analysisPct >= 20 &&
    analysisPct <= 40
  ) {
    standards.cognitiveDistribution = true;
    totalScore += 15;
  } else {
    failures.push(
      `Cognitive distribution off: Recall ${recallPct.toFixed(
        0
      )}%, Application ${applicationPct.toFixed(0)}%, Analysis ${analysisPct.toFixed(
        0
      )}% (Target: 30/40/30)`
    );
  }

  // Standard 7: Factual Accuracy (has explanations, 4 options each)
  const hasProperStructure = questions.every(
    (q) =>
      q.options.length === 4 &&
      q.correctAnswer >= 0 &&
      q.correctAnswer <= 3 &&
      q.explanation &&
      q.explanation.length > 10
  );

  if (hasProperStructure) {
    standards.factualAccuracy = true;
    totalScore += 15;
  } else {
    failures.push("Questions must have 4 options, valid answer index, and explanations");
  }

  const passedStandards = Object.keys(standards).filter(
    (key) => standards[key as keyof typeof standards]
  );

  return {
    passed: totalScore >= 70, // 70% threshold
    score: totalScore,
    passedStandards,
    failures,
  };
}

/**
 * Generate AI questions with retry mechanism (Phase 2 Wrapper)
 * Uses validated content from Phase 1
 * Includes retry logic with countdown timer on failures
 */
async function generateAIQuestions() {
  currentStep.value = "generating";
  isGenerating.value = true;

  // ✅ RETRY MECHANISM: Try up to MAX_RETRIES times with countdown between failures
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      await attemptGenerateAIQuestions(attempt, MAX_RETRIES);
      // Success - reset failed attempts and exit
      resetFailedAttempts();
      return;
    } catch (error: any) {
      logError(`Pre-test generation attempt ${attempt}/${MAX_RETRIES} failed`, error);

      // Record this failed attempt (clamped inside the function)
      recordFailedTestAttempt();

      // If we've reached the maximum retries, show final message and redirect
      if (failedTestAttempts.value >= MAX_RETRIES || attempt >= MAX_RETRIES) {
        logWarn("Final pre-test generation attempt failed - activating rate limit", {
          failedAttempts: failedTestAttempts.value,
          nextAttemptDelay: calculateTestAttemptDelay(failedTestAttempts.value),
        });

        // Prepare final-only modal (Cancel only)
        await Swal.fire({
          icon: "warning",
          title: `<span style="font-size: 20px; font-weight: 700;">Attempt ${Math.min(
            attempt,
            MAX_RETRIES
          )}/${MAX_RETRIES} Failed</span>`,
          html: `
            <div style="text-align: left; padding: 0;">
              <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #f59e0b; border-radius: 12px; padding: 14px; margin-bottom: 12px; box-shadow: 3px 3px 0 0 rgba(245, 158, 11, 0.15);">
                <p style="margin:0; font-size:14px; color:#92400e; font-weight:600; line-height:1.4;">${
                  error.message ||
                  "The pre-test question generation service is currently unavailable. Please try again later."
                }</p>
              </div>
              <div style="text-align:center; font-size:14px; color:#374151;">No retries remaining. You will be redirected back to the assessment screen.</div>
            </div>
          `,
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonText: `<span style="font-weight:600;">Close</span>`,
          cancelButtonColor: "#6b7280",
          customClass: {
            popup: "elevated-modal",
            title: "elevated-modal-title elevated-modal-title-no-padding",
          },
          width: "min(540px, 90vw)",
        });

        // Redirect user back
        isGenerating.value = false;
        currentStep.value = "intro";
        return;
      }

      // Otherwise allow retry flow for non-final failures
      // Check if now rate limited after recording failure
      const now = Date.now();
      const timeSinceLastAttempt = now - lastTestAttemptTime.value;
      const requiredDelay = calculateTestAttemptDelay(failedTestAttempts.value);
      const remainingDelay = Math.max(0, requiredDelay - timeSinceLastAttempt);

      // Use the loop `attempt` value for display (accurate to the current failed attempt)
      const currentAttemptNumber = Math.min(attempt, MAX_RETRIES);
      const remainingAttempts = Math.max(0, MAX_RETRIES - attempt);

      const retry = await Swal.fire({
        icon: "warning",
        title: `<span style="font-size: 20px; font-weight: 700;">Attempt ${currentAttemptNumber}/${MAX_RETRIES} Failed</span>`,
        html: `
          <div style="text-align: left; padding: 0;">
            <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #f59e0b; border-radius: 12px; padding: 14px; margin-bottom: ${
              remainingDelay > 0 ? "16px" : "20px"
            }; box-shadow: 3px 3px 0 0 rgba(245, 158, 11, 0.15);">
              <div style="display: flex; align-items: start; gap: 12px;">
                <div style="flex-shrink: 0; width: 24px; height: 24px; background: #f59e0b; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-top: 2px;">
                  <svg style="width: 14px; height: 14px; color: white;" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <div style="flex: 1;">
                  <p style="margin: 0; font-size: 14px; color: #92400e; font-weight: 600; line-height: 1.5;">${
                    error.message ||
                    "The pre-test question generation service is currently unavailable. Please try again."
                  }</p>
                </div>
              </div>
            </div>

            ${
              remainingDelay > 0
                ? `
            <div id="countdown-container" style="background: white; border: 2px solid #f97316; border-radius: 12px; padding: 16px; margin-bottom: 16px; box-shadow: 4px 4px 0 0 rgba(249, 115, 22, 0.1); text-align: center; transition: all 0.3s ease;">
              <p style="margin: 0 0 12px 0; font-size: 14px; color: #9a3412; font-weight: 600;">Please wait before retrying</p>
              <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 10px;">
                <svg style="width: 28px; height: 28px; color: #f97316;" class="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style="opacity: 0.25;" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
                  <path style="opacity: 0.75;" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <div>
                  <div style="display: flex; align-items: baseline; gap: 4px;">
                    <span style="font-size: 48px; font-weight: 800; color: #ea580c; line-height: 1;" id="countdown-timer">${Math.ceil(
                      remainingDelay / 1000
                    )}</span>
                    <span style="font-size: 16px; color: #9a3412; font-weight: 600;">sec</span>
                  </div>
                </div>
              </div>
              <div style="background: #ffedd5; border-radius: 8px; padding: 8px; border: 1px solid #fed7aa;">
                <p style="margin: 0; font-size: 12px; color: #9a3412; font-weight: 500; line-height: 1.4;">Buttons will enable when timer reaches zero</p>
              </div>
            </div>
            `
                : ""
            }

            <div style="text-align: center;">
              <p style="margin: 0; font-size: 15px; color: #374151; font-weight: 500;">Would you like to retry? <span style="color: #f59e0b; font-weight: 700;">(${remainingAttempts} ${
          remainingAttempts === 1 ? "attempt" : "attempts"
        } remaining)</span></p>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: `<span style="font-weight: 600;">Retry Now (${remainingAttempts} left)</span>`,
        cancelButtonText: `<span style="font-weight: 600;">Cancel</span>`,
        confirmButtonColor: "#f59e0b",
        cancelButtonColor: "#6b7280",
        customClass: {
          popup: "elevated-modal",
          title: "elevated-modal-title elevated-modal-title-no-padding",
        },
        width: "min(540px, 90vw)",
        didOpen: () => {
          const titleElement = document.getElementById("swal2-title");
          if (titleElement) {
            titleElement.style.padding = "0";
            titleElement.style.paddingTop = "0";
            titleElement.style.paddingRight = "0";
            titleElement.style.paddingBottom = "0";
            titleElement.style.paddingLeft = "0";
            titleElement.style.setProperty("--swal2-title-padding", "0", "important");
          }

          if (remainingDelay > 0) {
            const confirmButton = Swal.getConfirmButton();
            const cancelButton = Swal.getCancelButton();
            const timerElement = document.getElementById("countdown-timer");
            const countdownContainer = document.getElementById("countdown-container");

            if (confirmButton) confirmButton.disabled = true;
            if (cancelButton) cancelButton.disabled = true;

            let secondsLeft = Math.ceil(remainingDelay / 1000);

            const countdownInterval = setInterval(() => {
              secondsLeft--;
              if (timerElement) timerElement.textContent = String(secondsLeft);

              if (secondsLeft <= 0) {
                clearInterval(countdownInterval);

                if (countdownContainer) {
                  countdownContainer.style.opacity = "0";
                  countdownContainer.style.maxHeight = countdownContainer.offsetHeight + "px";
                  countdownContainer.style.overflow = "hidden";

                  setTimeout(() => {
                    countdownContainer.style.maxHeight = "0";
                    countdownContainer.style.marginBottom = "0";
                    countdownContainer.style.padding = "0";
                    countdownContainer.style.border = "none";
                  }, 150);
                }

                if (confirmButton) confirmButton.disabled = false;
                if (cancelButton) cancelButton.disabled = false;
              }
            }, 1000);
          }
        },
      });

      if (!retry.isConfirmed) {
        isGenerating.value = false;
        currentStep.value = "intro";
        return;
      } else {
        // User chose to retry: ensure modal stays in generating state
        isGenerating.value = true;
        currentStep.value = "generating";
      }

      // Exponential backoff: 2s, 4s, 8s
      const delay = Math.pow(2, attempt) * 1000;
      logInfo(`Waiting ${delay}ms before retry ${attempt + 1}`, {
        attempt: attempt + 1,
        maxRetries: MAX_RETRIES,
      });

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

/**
 * Actual AI question generation logic (Phase 2)
 * Includes duplicate detection, source search, and source scraping for fact-checking
 */
async function attemptGenerateAIQuestions(attempt: number, maxRetries: number) {
  // Track Phase 2 start time
  phase2StartTime.value = new Date().toISOString();

  try {
    logInfo("PHASE 2: Generating pre-test questions with Gemini", {
      subject: props.sessionSubject,
      contentLength: extractedContent.value.length,
      language: contentLanguage.value,
      attempt,
      maxRetries,
    });

    // ========================================
    // STEP 5/13: DUPLICATE DETECTION (matches PostTestModal)
    // ========================================
    currentAIStep.value = 5;
    currentAIStepMessage.value = "🔍 Checking for duplicate content...";
    aiProgress.value = 38;

    logInfo("🔍 Step 5/13: Checking for duplicate content", {
      contentLength: extractedContent.value.length,
      sessionId: props.sessionId,
    });

    try {
      const { duplicateDetectionService } = await import("@/services/duplicateDetectionService");

      const isDuplicate = await duplicateDetectionService.checkForDuplicates(
        extractedContent.value,
        "pre_test",
        props.sessionId
      );
      if (isDuplicate.isDuplicate) {
        const similarityPercent =
          isDuplicate.similarityScore !== undefined
            ? (isDuplicate.similarityScore * 100).toFixed(1)
            : "N/A";

        logWarn("❌ Duplicate content detected", {
          similarityScore: isDuplicate.similarityScore,
        });

        const shouldContinue = await Swal.fire({
          icon: "warning",
          title: "Duplicate Content Detected",
          html: `
            <div class="text-left">
              <p class="mb-3"><strong>This content has already been used before:</strong></p>
              <div class="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 mb-3">
                <p class="mb-2"><strong>Similarity:</strong> <span class="text-orange-700">${similarityPercent}%</span></p>
                <p><strong>Type:</strong> <span class="text-gray-700">Pre-Test</span></p>
              </div>
              <p class="mb-3">Using the same content multiple times may not provide accurate baseline assessment.</p>
              <p class="text-sm text-gray-600">Do you want to continue anyway?</p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Continue Anyway",
          cancelButtonText: "Cancel",
        });

        if (!shouldContinue.isConfirmed) {
          currentStep.value = "intro";
          return;
        }

        logInfo("User chose to continue despite duplicate content");
      } else {
        logSuccess("✅ No duplicate content found", {
          similarityScore: isDuplicate.similarityScore || 0,
        });
      }
    } catch (duplicateError) {
      logError("Failed to check for duplicates, continuing anyway", duplicateError);
      // Don't block generation if duplicate detection fails
    }

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 6/13: EXTRACT KEY TOPICS
    // ========================================
    currentAIStep.value = 6;
    currentAIStepMessage.value = "🔍 Extracting key topics from content...";
    aiProgress.value = 46;

    logInfo("🔍 Step 6/13: Extracting key topics for source search", {
      contentLength: extractedContent.value.length,
    });

    const keyTopics = extractedContent.value
      .split(/[.!?]/)
      .slice(0, 3)
      .join(" ")
      .split(/\s+/)
      .filter((word) => word.length > 4)
      .slice(0, 10);

    logDebug("Extracted key topics", { topics: keyTopics });

    // ========================================
    // STEP 7/13: SEARCH CREDIBLE SOURCES
    // ========================================
    currentAIStep.value = 7;
    currentAIStepMessage.value = "🔎 Searching credible educational sources...";
    aiProgress.value = 54;

    logInfo("🔍 Step 7/13: Searching credible educational sources", {
      topicsCount: keyTopics.length,
      subject: props.sessionSubject,
    });

    let credibleSources: string[] = [];
    try {
      const { backendAIService } = await import("@/services/backendAIService");

      credibleSources = await backendAIService.searchCredibleSources(
        keyTopics.slice(0, 5),
        props.sessionSubject
      );

      logSuccess(`✅ Step 7/13: Found ${credibleSources.length} credible sources`, {
        sources: credibleSources.slice(0, 3),
      });
    } catch (err) {
      logWarn("⚠️ Step 7/13: Source search failed, continuing without external sources", err);
    }

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 7.5/13: RERANK SOURCES WITH HUGGINGFACE JINA AI
    // ========================================
    currentAIStep.value = 7.5;
    currentAIStepMessage.value = "🎯 Reranking sources by relevance with AI...";
    aiProgress.value = 58;

    let rerankedSources: string[] = [];
    let rerankingMetrics = {
      inputSources: credibleSources.length,
      rerankedSources: 0,
      topScore: 0,
      avgScore: 0,
      filteredOutCount: 0,
    };

    if (credibleSources.length > 0) {
      logInfo("🔍 Step 7.5/13: Reranking sources with HuggingFace Jina AI", {
        sourcesCount: credibleSources.length,
      });

      try {
        const { backendAIService } = await import("@/services/backendAIService");

        // Rerank sources based on relevance to subject + key topics
        const rerankQuery = `${props.sessionSubject}: ${keyTopics.slice(0, 5).join(" ")}`;

        // Convert string URLs to objects with url property
        const sourcesToRerank = credibleSources.map((url) => ({ url }));

        const rerankedResults = await backendAIService.rerankSources(
          rerankQuery,
          sourcesToRerank,
          5, // Top 5 most relevant sources
          0.15 // Minimum relevance score threshold (lowered to accept more sources)
        );

        rerankedSources = rerankedResults.map((r: any) => r.url);

        // Calculate metrics
        rerankingMetrics.rerankedSources = rerankedSources.length;
        rerankingMetrics.topScore = rerankedResults[0]?.score || 0;
        rerankingMetrics.avgScore =
          rerankedResults.reduce((sum: number, r: any) => sum + r.score, 0) /
          rerankedResults.length;
        rerankingMetrics.filteredOutCount = credibleSources.length - rerankedSources.length;

        logSuccess(
          `✅ Step 7.5/13: Sources reranked by relevance (kept ${rerankedSources.length}/${credibleSources.length})`,
          {
            topScore: rerankingMetrics.topScore.toFixed(3),
            avgScore: rerankingMetrics.avgScore.toFixed(3),
            filteredOut: rerankingMetrics.filteredOutCount,
          }
        );
      } catch (err) {
        logWarn("⚠️ Step 7.5/13: Reranking failed, using original source order", err);
        rerankedSources = credibleSources.slice(0, 5); // Fallback to first 5
        rerankingMetrics.rerankedSources = rerankedSources.length;
      }
    } else {
      logWarn("⚠️ Step 7.5/13: No sources to rerank");
    }

    // Use reranked sources for next steps
    credibleSources = rerankedSources.length > 0 ? rerankedSources : credibleSources;

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 7.75/13: VALIDATE URLS BEFORE SCRAPING
    // ========================================
    currentAIStep.value = 7.75;
    currentAIStepMessage.value = "🔍 Validating source URLs accessibility...";
    aiProgress.value = 60;

    let validatedUrls: string[] = [];
    let urlValidationMetrics = {
      totalUrls: credibleSources.length,
      validUrls: 0,
      invalidUrls: 0,
      timeoutUrls: 0,
    };

    if (credibleSources.length > 0) {
      logInfo("🔍 Step 7.75/13: Validating URL accessibility before scraping", {
        urlsCount: credibleSources.length,
      });

      try {
        const { backendAIService } = await import("@/services/backendAIService");

        const validationResults = await backendAIService.validateUrls(
          credibleSources.slice(0, 5), // Validate top 5
          5000 // 5 second timeout per URL
        );

        validatedUrls = validationResults.filter((r: any) => r.isValid).map((r: any) => r.url);

        // Calculate metrics
        urlValidationMetrics.validUrls = validatedUrls.length;
        urlValidationMetrics.invalidUrls = credibleSources.length - validatedUrls.length;
        urlValidationMetrics.timeoutUrls = validationResults.filter((r: any) =>
          r.error?.includes("timeout")
        ).length;

        logSuccess(
          `✅ Step 7.75/13: URLs validated (${validatedUrls.length}/${credibleSources.length} accessible)`,
          {
            valid: urlValidationMetrics.validUrls,
            invalid: urlValidationMetrics.invalidUrls,
            timeouts: urlValidationMetrics.timeoutUrls,
          }
        );
      } catch (err) {
        logWarn("⚠️ Step 7.75/13: URL validation failed, proceeding with all URLs", err);
        validatedUrls = credibleSources.slice(0, 5); // Fallback
        urlValidationMetrics.validUrls = validatedUrls.length;
      }
    } else {
      logWarn("⚠️ Step 7.75/13: No URLs to validate");
    }

    // Use only validated URLs for scraping
    credibleSources = validatedUrls.length > 0 ? validatedUrls : credibleSources;

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 8/13: SCRAPE CONTENT FROM SOURCES (CRITICAL FOR FACT-CHECKING)
    // ========================================
    currentAIStep.value = 8;
    currentAIStepMessage.value = "📚 Scraping educational content for fact-checking...";
    aiProgress.value = 62;

    let scrapedSourceContent: Array<{
      url: string;
      content: string;
      title: string;
      word_count: number;
    }> = [];

    let scrapingMetrics = {
      attemptedUrls: credibleSources.length,
      successfulScrapes: 0,
      failedScrapes: 0,
      totalWordsScraped: 0,
      avgWordsPerSource: 0,
      scrapingTimeMs: 0,
    };

    if (credibleSources.length > 0) {
      logInfo("🔍 Step 8/13: Scraping educational content for fact-checking", {
        sourcesCount: credibleSources.length,
      });

      const scrapingStartTime = performance.now();

      try {
        const { backendAIService } = await import("@/services/backendAIService");

        const scrapedSources = await backendAIService.scrapeSources(
          credibleSources.slice(0, 3), // Top 3 sources only
          3
        );

        scrapedSourceContent = scrapedSources;

        // Calculate scraping metrics
        scrapingMetrics.scrapingTimeMs = performance.now() - scrapingStartTime;
        scrapingMetrics.successfulScrapes = scrapedSourceContent.length;
        scrapingMetrics.failedScrapes = Math.max(
          0,
          credibleSources.slice(0, 3).length - scrapedSourceContent.length
        );
        scrapingMetrics.totalWordsScraped = scrapedSourceContent.reduce(
          (sum, s) => sum + s.word_count,
          0
        );
        scrapingMetrics.avgWordsPerSource =
          scrapedSourceContent.length > 0
            ? Math.round(scrapingMetrics.totalWordsScraped / scrapedSourceContent.length)
            : 0;

        if (scrapedSourceContent.length > 0) {
          logSuccess(
            `✅ Step 8/13: Scraped ${
              scrapedSourceContent.length
            } sources (${scrapingMetrics.totalWordsScraped.toLocaleString()} words in ${scrapingMetrics.scrapingTimeMs.toFixed(
              0
            )}ms)`,
            {
              sources: scrapedSourceContent.map((s) => ({
                title: s.title,
                words: s.word_count.toLocaleString(),
              })),
              avgWords: scrapingMetrics.avgWordsPerSource.toLocaleString(),
              failedScrapes: scrapingMetrics.failedScrapes,
            }
          );
        } else {
          logWarn("⚠️ Step 8/13: No content could be scraped from sources");
        }
      } catch (err) {
        logError("❌ Step 8/13: Source scraping failed", err);
        scrapingMetrics.failedScrapes = credibleSources.slice(0, 3).length;
        scrapingMetrics.scrapingTimeMs = performance.now() - scrapingStartTime;
      }
    } else {
      logWarn("⚠️ Step 8/13: No sources to scrape, proceeding without fact-checking content");
    }

    // Store comprehensive source pipeline analytics
    sourceAnalytics.value = {
      searchResults: {
        totalSources: rerankingMetrics.inputSources,
        rerankedSources: rerankingMetrics.rerankedSources,
        topRelevanceScore: rerankingMetrics.topScore,
        avgRelevanceScore: rerankingMetrics.avgScore,
        filteredOutCount: rerankingMetrics.filteredOutCount,
      },
      urlValidation: {
        totalUrls: urlValidationMetrics.totalUrls,
        validUrls: urlValidationMetrics.validUrls,
        invalidUrls: urlValidationMetrics.invalidUrls,
        timeoutUrls: urlValidationMetrics.timeoutUrls,
      },
      scraping: {
        attemptedUrls: scrapingMetrics.attemptedUrls,
        successfulScrapes: scrapingMetrics.successfulScrapes,
        failedScrapes: scrapingMetrics.failedScrapes,
        totalWordsScraped: scrapingMetrics.totalWordsScraped,
        avgWordsPerSource: scrapingMetrics.avgWordsPerSource,
        scrapingTimeMs: scrapingMetrics.scrapingTimeMs,
      },
    };

    logInfo("📊 Source pipeline analytics stored", sourceAnalytics.value);

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 9-12/13: GENERATE QUESTIONS WITH GEMINI (WITH SCRAPED SOURCES)
    // ========================================
    currentAIStep.value = 9;
    currentAIStepMessage.value = "🤖 Delegating to Gemini AI with fact-checking...";
    aiProgress.value = 70;

    logInfo("🔍 Step 9-12/13: Delegating to Gemini with scraped source content", {
      contentLength: extractedContent.value.length,
      scrapedSources: scrapedSourceContent.length,
      hasFactCheckingContent: scrapedSourceContent.length > 0,
    });

    // Import geminiService
    const { geminiService } = await import("@/services/geminiService");

    // Update progress for Step 10/13
    setTimeout(() => {
      if (currentAIStep.value === 9) {
        currentAIStep.value = 10;
        currentAIStepMessage.value = "🧠 Analyzing content and formulating questions...";
        aiProgress.value = 77;
      }
    }, 3000);

    // Update progress for Step 11/13
    setTimeout(() => {
      if (currentAIStep.value === 10) {
        currentAIStep.value = 11;
        currentAIStepMessage.value = "📝 Generating multiple-choice options...";
        aiProgress.value = 85;
      }
    }, 6000);

    // Update progress for Step 12/13
    setTimeout(() => {
      if (currentAIStep.value === 11) {
        currentAIStep.value = 12;
        currentAIStepMessage.value = "🎯 Finalizing questions and answers...";
        aiProgress.value = 92;
      }
    }, 9000);

    // ✅ CRITICAL: Generate pre-test questions WITH SCRAPED SOURCES for fact-checking
    const result = await geminiService.generatePreTest(
      props.sessionSubject,
      props.learningGoals || "",
      extractedContent.value, // Validated content from Phase 1
      props.materials?.length ? props.materials[0].name : undefined,
      contentLanguage.value === "filipino" ? "filipino" : "english",
      scrapedSourceContent.length > 0 ? scrapedSourceContent : undefined // ✅ CRITICAL: Pass scraped content!
    );

    // Store generated questions
    testQuestions.value = result.questions;
    testTopic.value = result.topic;
    testSubject.value = result.subject;
    testSources.value = result.sources;

    logSuccess("✅ Step 9-12/13: AI Generation Complete - Pre-test questions generated", {
      questionCount: testQuestions.value.length,
      topic: testTopic.value,
      sourcesCount: testSources.value.length,
    });

    // Performance: Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    // ========================================
    // STEP 13/13: QUALITY VALIDATION (7 Standards)
    // ========================================
    currentAIStep.value = 13;
    currentAIStepMessage.value = "✅ Validating question quality and preparing test...";
    aiProgress.value = 100;

    logInfo("🔍 Step 13/13: Running quality validation checks (7 standards)...");
    const qualityResults = validateQuestionQuality(testQuestions.value, props.sessionSubject);

    // Store quality validation results for Firestore
    qualityValidationResults.value = qualityResults;

    if (!qualityResults.passed) {
      logWarn("⚠️ Step 13/13: Quality validation failed", {
        failedStandards: qualityResults.failures,
        score: qualityResults.score,
      });

      // Show warning but allow to proceed
      await Swal.fire({
        icon: "warning",
        title: "Quality Check Warning",
        html: `
          <p>Some quality standards were not fully met:</p>
          <ul class="text-left text-sm mt-2">
            ${qualityResults.failures.map((f) => `<li>• ${f}</li>`).join("")}
          </ul>
          <p class="mt-2 text-sm">The questions are still usable, but may not be optimal.</p>
        `,
        confirmButtonText: "Continue Anyway",
        showCancelButton: true,
        cancelButtonText: "Regenerate",
      }).then((result) => {
        if (!result.isConfirmed) {
          // User wants to regenerate
          return generateAIQuestions();
        }
      });
    } else {
      logSuccess("✅ Step 13/13: Quality validation passed - All 7 standards met", {
        score: qualityResults.score,
        standards: qualityResults.passedStandards,
      });
    }

    // Initialize answers array
    answers.value = new Array(testQuestions.value.length).fill(-1);

    // Track Phase 2 end time
    phase2EndTime.value = new Date().toISOString();

    logSuccess("✅ PHASE 2 COMPLETE - Pre-test ready for student", {
      questionCount: testQuestions.value.length,
      topic: testTopic.value,
      qualityScore: qualityResults.score,
      duration: `${
        new Date(phase2EndTime.value).getTime() - new Date(phase2StartTime.value).getTime()
      }ms`,
    });

    // Move to questions step
    currentStep.value = "questions";
    isGenerating.value = false;
  } catch (error) {
    logError(`Pre-test generation attempt ${attempt}/${maxRetries} failed`, error);
    // Re-throw to trigger retry logic in parent function
    throw error;
  }
}

// ========================================
// PHASE 2: QUESTION INTERACTION & SUBMISSION
// ========================================

/**
 * Select an answer for the current question
 */
function selectAnswer(answerIndex: number) {
  answers.value[currentQuestion.value] = answerIndex;
  logDebug("Answer selected", {
    question: currentQuestion.value + 1,
    answer: answerIndex,
  });
}

/**
 * Navigate to previous question
 */
function previousQuestion() {
  if (currentQuestion.value > 0) {
    currentQuestion.value--;
  }
}

/**
 * Navigate to next question or submit
 */
function nextQuestion() {
  if (currentQuestion.value < testQuestions.value.length - 1) {
    currentQuestion.value++;
  } else {
    // Last question - submit test
    submitPreTest();
  }
}

/**
 * Check if current question is answered
 */
const isCurrentQuestionAnswered = computed(() => {
  return answers.value[currentQuestion.value] !== -1;
});

/**
 * Calculate the test score
 */
function calculateScore(): number {
  let correct = 0;
  testQuestions.value.forEach((question, index) => {
    if (answers.value[index] === question.correctAnswer) {
      correct++;
    }
  });
  return correct;
}

/**
 * Calculate cognitive distribution from questions
 */
function calculateCognitiveDistribution() {
  let recallCount = 0;
  let applicationCount = 0;
  let analysisCount = 0;

  const recallKeywords = [
    "who",
    "what",
    "when",
    "where",
    "define",
    "list",
    "name",
    "identify",
    "ano",
    "sino",
    "kailan",
    "saan",
    "tukuyin",
    "sabihin",
  ];
  const applicationKeywords = [
    "how",
    "apply",
    "solve",
    "calculate",
    "use",
    "demonstrate",
    "show",
    "paano",
    "gamitin",
    "lutasin",
    "ipakita",
    "kalkulahin",
  ];
  const analysisKeywords = [
    "why",
    "analyze",
    "compare",
    "contrast",
    "explain",
    "evaluate",
    "assess",
    "bakit",
    "suriin",
    "ihambing",
    "ipaliwanag",
    "tasa",
  ];

  testQuestions.value.forEach((q) => {
    const questionLower = q.question.toLowerCase();
    if (recallKeywords.some((kw) => questionLower.includes(kw))) {
      recallCount++;
    } else if (analysisKeywords.some((kw) => questionLower.includes(kw))) {
      analysisCount++;
    } else {
      // Default to application if no clear markers
      applicationCount++;
    }
  });

  const total = testQuestions.value.length;
  return {
    recallCount,
    recallPercentage: Math.round((recallCount / total) * 100),
    applicationCount,
    applicationPercentage: Math.round((applicationCount / total) * 100),
    analysisCount,
    analysisPercentage: Math.round((analysisCount / total) * 100),
  };
}

/**
 * Submit pre-test to Firestore with comprehensive analytics
 */
async function submitPreTest() {
  isSubmitting.value = true;

  try {
    logInfo("Submitting pre-test to Firestore", {
      sessionId: props.sessionId,
      questionCount: testQuestions.value.length,
    });

    const score = calculateScore();
    const percentage = Math.round((score / testQuestions.value.length) * 100);
    const cognitiveDistribution = calculateCognitiveDistribution();

    // Handle rate limiting based on test result
    const passingScore = Math.ceil(testQuestions.value.length * 0.6); // 60% passing threshold
    const passed = score >= passingScore;

    if (passed) {
      // Reset failed attempts on successful pass
      resetFailedAttempts();
      logSuccess("Pre-test passed - failed attempts reset", {
        score,
        percentage,
        passingScore,
      });
    } else {
      // Record failed attempt for rate limiting
      recordFailedTestAttempt();
      logWarn("Pre-test failed - attempt recorded for rate limiting", {
        score,
        percentage,
        passingScore,
        failedAttempts: failedTestAttempts.value,
      });
    }

    // ✅ UPLOAD TO FIREBASE STORAGE (moved from generation to submission for consistency with PostTest)
    let processedContentUrl = "";
    if (extractedContent.value && props.materials && props.materials.length > 0) {
      try {
        logInfo("📁 Uploading processed content to Firebase Storage...");

        const { ref: storageRef, uploadString, getDownloadURL } = await import("firebase/storage");
        const { storage } = await import("@/firebase");

        const contentMetadata = {
          sessionId: props.sessionId,
          subject: props.sessionSubject,
          language: contentLanguage.value,
          processedAt: new Date().toISOString(),
          originalFile: props.materials[0].name,
          contentLength: extractedContent.value.length,
          questionCount: testQuestions.value.length,
        };

        const storageReference = storageRef(
          storage,
          `preTests/${auth.currentUser?.uid}/${props.sessionId}/processed_content.txt`
        );

        const contentWithMetadata = `
=================================================
PRE-TEST PROCESSED CONTENT
=================================================
Session ID: ${props.sessionId}
Subject: ${props.sessionSubject}
Language: ${contentLanguage.value}
Processed At: ${new Date().toLocaleString()}
Original File: ${props.materials[0].name}
Content Length: ${extractedContent.value.length} characters
Generated Questions: ${testQuestions.value.length}
=================================================

${extractedContent.value}

=================================================
METADATA
=================================================
${JSON.stringify(contentMetadata, null, 2)}
`;

        await uploadString(storageReference, contentWithMetadata);
        processedContentUrl = await getDownloadURL(storageReference);

        logSuccess("✅ Content uploaded to Firebase Storage", {
          path: storageReference.fullPath,
          url: processedContentUrl,
        });
      } catch (error) {
        logError("❌ Firebase Storage upload failed (non-critical)", error);
        // Continue even if storage upload fails
      }
    }

    // Prepare comprehensive results for Firestore
    const preTestData = {
      // ========== Core Test Data ==========
      sessionId: props.sessionId,
      subject: testSubject.value || props.sessionSubject, // Fallback to props if not set
      topic: testTopic.value || "General Assessment", // Fallback topic
      studentNotes: props.learningGoals || "", // Save student notes from session
      materials:
        props.materials?.map((m) => ({
          name: m.name,
          url: m.url,
          type: m.type,
          // ✅ CRITICAL: Remove originalFile - Firestore can't store File objects
        })) || [], // Save materials from session (without File objects)
      source_type:
        props.materials && props.materials.length > 0
          ? props.materials[0].type.includes("pdf")
            ? "pdf"
            : props.materials[0].type.includes("doc")
            ? "docx"
            : "txt"
          : "note", // Set source type based on materials or default to 'note'
      input_length: extractedContent.value?.length || 0, // Content length from extracted content
      ...(props.materials && props.materials.length > 0 && { filename: props.materials[0].name }), // ✅ FIX: Only include filename if it exists (avoid undefined)
      questions: testQuestions.value.map((q, index) => ({
        question: q.question || "",
        options: q.options || [],
        correctAnswer: q.correctAnswer ?? 0, // Use nullish coalescing to handle 0
        userAnswer: answers.value[index] ?? -1,
        isCorrect: answers.value[index] === q.correctAnswer,
        explanation: q.explanation || "",
      })),
      score,
      totalQuestions: testQuestions.value.length,
      percentage,
      language: contentLanguage.value || "english", // Fallback to english
      sources: testSources.value || [],
      date_completed: Timestamp.now(), // ✅ CONSISTENCY FIX: Use date_completed to match preTestService.ts
      userId: auth.currentUser?.uid || "", // For backward compatibility
      studentId: auth.currentUser?.uid || "", // ✅ FIX: Required by Firestore rules
      processedContentUrl: processedContentUrl || null,

      // ========== Source Pipeline Analytics ==========
      sourceAnalytics: sourceAnalytics.value || {
        searchResults: {
          totalSources: 0,
          rerankedSources: 0,
          topRelevanceScore: 0,
          avgRelevanceScore: 0,
          filteredOutCount: 0,
        },
        urlValidation: {
          totalUrls: 0,
          validUrls: 0,
          invalidUrls: 0,
          timeoutUrls: 0,
        },
        scraping: {
          attemptedUrls: 0,
          successfulScrapes: 0,
          failedScrapes: 0,
          totalWordsScraped: 0,
          avgWordsPerSource: 0,
          scrapingTimeMs: 0,
        },
      },

      // ========== Generation Metrics ==========
      generationMetrics: {
        phase1: {
          startTime: phase1StartTime.value,
          endTime: phase1EndTime.value,
          durationMs: phase1EndTime.value
            ? new Date(phase1EndTime.value).getTime() - new Date(phase1StartTime.value).getTime()
            : 0,
          extractionMethod: extractionMethod.value,
          contentLength: extractedContent.value.length,
          languageDetected: contentLanguage.value,
          languageConfidence: languageConfidence.value,
        },
        phase2: {
          startTime: phase2StartTime.value,
          endTime: phase2EndTime.value,
          durationMs: phase2EndTime.value
            ? new Date(phase2EndTime.value).getTime() - new Date(phase2StartTime.value).getTime()
            : 0,
          qualityScore: qualityValidationResults.value?.score || 0,
          qualityPassed: qualityValidationResults.value?.passed || false,
          cognitiveDistribution: cognitiveDistribution,
        },
      },
    };

    // ✅ VALIDATION: Check if user is authenticated before saving
    if (!auth.currentUser) {
      throw new Error("User not authenticated. Please log in and try again.");
    }

    // ✅ VALIDATION: Check if we have all required data
    if (!props.sessionId || !testQuestions.value || testQuestions.value.length === 0) {
      throw new Error("Missing required data. Please restart the pre-test.");
    }

    logDebug("Pre-test data validation passed", {
      userId: auth.currentUser.uid,
      sessionId: props.sessionId,
      questionsCount: testQuestions.value.length,
      answersCount: answers.value.filter((a) => a !== -1).length,
    });

    // Save to Firestore
    // ✅ FIX: For validators (testing), generate unique IDs instead of using sessionId
    // This allows unlimited pre-tests without overwriting
    let preTestRef;

    if (isValidator.value) {
      // Generate unique ID for validator tests (timestamp + random)
      const uniqueId = `validator_${props.sessionId}_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      preTestRef = doc(db, "preTests", uniqueId);
      logInfo("Validator detected - using unique document ID to allow unlimited tests", {
        uniqueId,
      });
    } else {
      // For real students, use sessionId (one pre-test per session)
      preTestRef = doc(db, "preTests", props.sessionId);
      logInfo("Student detected - using sessionId as document ID", { sessionId: props.sessionId });
    }

    logInfo("Attempting to save pre-test to Firestore...", {
      docPath: `preTests/${isValidator.value ? "validator_*" : props.sessionId}`,
      dataSize: JSON.stringify(preTestData).length,
    });

    await setDoc(preTestRef, preTestData);

    logSuccess("Pre-test data successfully written to Firestore", {
      docPath: preTestRef.path,
    });

    // ✅ Store data for later emission when user closes the review
    completedPreTestData.value = preTestData;

    logSuccess("Pre-test submitted successfully", {
      sessionId: props.sessionId,
      score: `${score}/${testQuestions.value.length}`,
      percentage: `${percentage}%`,
    });

    // Clear pending pre-test from localStorage
    clearPendingPreTest();

    // ✅ Show compact score popup with option to review answers
    await Swal.fire({
      icon: "success",
      title: "🎉 Pre-Test Complete!",
      html: `
        <div class="text-left">
          <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 mb-3">
            <p class="text-center"><strong class="text-3xl text-blue-600">${percentage}%</strong></p>
            <p class="text-center text-sm text-gray-700">You got ${score} out of ${testQuestions.value.length} correct!</p>
          </div>
          <div class="bg-purple-50 border-2 border-purple-300 rounded-lg p-3">
            <p class="text-xs font-semibold text-purple-900 mb-1">✅ What's Next:</p>
            <ul class="list-disc list-inside text-xs text-purple-800 space-y-0.5">
              <li>Review your answers to see your baseline knowledge</li>
              <li>Your tutor will tailor the session to your needs</li>
            </ul>
          </div>
        </div>
      `,
      confirmButtonText: "Review My Answers",
      allowOutsideClick: false,
      customClass: {
        popup: "elevated-modal",
        title: "elevated-modal-title",
      },
    });

    // Move to complete step (shows answer review)
    // ✅ FIX: Don't emit complete yet - wait until user closes the review
    currentStep.value = "complete";
  } catch (error) {
    logError("Failed to submit pre-test", error);

    // Get detailed error message
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorCode = (error as any)?.code || "unknown";

    logError("Detailed error info", {
      message: errorMessage,
      code: errorCode,
      userId: auth.currentUser?.uid,
      sessionId: props.sessionId,
    });

    // Show more helpful error message based on error type
    let userMessage = "Your pre-test results could not be saved to the database. Please try again.";
    let technicalDetails = "";

    if (errorCode === "permission-denied" || errorMessage.includes("permission")) {
      userMessage =
        "Access denied. Your account does not have permission to save pre-test results. This may indicate an account configuration issue. Please contact your administrator or support team.";
      technicalDetails = "Database permission denied. Your account role may need to be updated.";
    } else if (errorCode === "unauthenticated" || errorMessage.includes("auth")) {
      userMessage =
        "Your login session has expired. Please refresh this page and log in again to continue.";
      technicalDetails = "Authentication token expired or invalid. Re-login required.";
    } else if (
      errorMessage.includes("Failed to get document") ||
      errorMessage.includes("offline")
    ) {
      userMessage =
        "Your internet connection was lost. Please check your network connection and try submitting again.";
      technicalDetails = "Network connectivity issue detected. Database is unreachable.";
    } else {
      userMessage =
        "The database could not save your results due to a technical error. Please try again, or contact support if the problem persists.";
      technicalDetails = `Database error: ${errorMessage}`;
    }

    await Swal.fire({
      icon: "error",
      title: "Submission Failed",
      html: `
        <div class="text-left">
          <p class="mb-3">${userMessage}</p>
          ${
            technicalDetails
              ? `<p class="text-xs text-gray-600 bg-gray-100 p-2 rounded mt-2">${technicalDetails}</p>`
              : ""
          }
        </div>
      `,
      confirmButtonText: "OK",
      confirmButtonColor: "#3B82F6",
    });
  } finally {
    isSubmitting.value = false;
  }
}

/**
 * Handle exit request from user (Go Back button)
 * Shows confirmation dialog before allowing exit
 */
async function handleExitRequest() {
  // ✅ NEW: Prevent exit if mandatory (admin-created session)
  if (props.isMandatory) {
    await Swal.fire({
      icon: "warning",
      title: "Pre-Test Required",
      html: `
        <div class="text-left">
          <p class="mb-3">This pre-test is required to confirm your tutoring session.</p>
          <div class="bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <p class="text-sm text-red-900"><strong>⚠️ You cannot skip this assessment</strong></p>
            <p class="text-sm text-red-800 mt-2">
              Your tutor has created this session and it will only be confirmed after you complete the pre-test.
            </p>
          </div>
        </div>
      `,
      confirmButtonText: "Continue Pre-Test",
      confirmButtonColor: "#3b82f6",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    return;
  }

  const result = await Swal.fire({
    icon: "warning",
    title: "Exit Pre-Test?",
    html: `
      <div class="text-left">
        <p class="mb-3">Are you sure you want to go back?</p>
        <div class="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
          <p class="text-sm text-amber-900"><strong>⚠️ Important:</strong></p>
          <p class="text-sm text-amber-800 mt-2">
            You must complete the pre-test to successfully book your session. 
            The session will <strong>NOT be saved</strong> until you finish the assessment.
          </p>
        </div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: "Yes, Go Back",
    cancelButtonText: "Continue Pre-Test",
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#3b82f6",
    allowOutsideClick: false,
  });

  if (result.isConfirmed) {
    logInfo("User chose to exit pre-test - session will NOT be created");

    // Clear pending data
    clearPendingPreTest();

    // Emit close to return to BookSessionView
    emit("close");
    resetModal();
  }
}

function handleClose() {
  // Allow close after test complete
  if (currentStep.value === "complete") {
    // ✅ Set navigation state to show loading indicator
    isNavigating.value = true;

    // ✅ Emit complete event with test data when closing the review
    if (completedPreTestData.value) {
      emit("complete", completedPreTestData.value);
      // Don't emit close here - parent will handle navigation after complete event
    } else {
      emit("close"); // Fallback if no data
      isNavigating.value = false; // Reset if fallback
    }
  }
  // ✅ NEW: Prevent closing if mandatory and not complete
  else if (props.isMandatory) {
    Swal.fire({
      icon: "warning",
      title: "Pre-Test Required",
      text: "This pre-test is required to confirm your tutoring session. You must complete it.",
      confirmButtonText: "Continue Pre-Test",
      confirmButtonColor: "#3b82f6",
    });
  }
  // Prevent closing during test or generation
  else if (currentStep.value === "questions" || isGenerating.value) {
    Swal.fire({
      icon: "info",
      title: "Pre-Test In Progress",
      text: "Please complete the assessment before exiting.",
      confirmButtonText: "Continue",
    });
  }
}

// Mandatory blocking (prevent escape)
let isBlockingEscape = ref(false);

const setupMandatoryBlocking = () => {
  // ✅ Use props.isMandatory instead of local state
  isBlockingEscape.value = props.isMandatory;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (currentStep.value === "complete") return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // ✅ Warn users about reloading/closing page at ANY time during pre-test
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (currentStep.value !== "complete") {
      e.preventDefault();
      // Modern browsers require both preventDefault() and returnValue
      const message = "Changes you made may not be saved.";
      e.returnValue = message;
      return message; // For older browsers
    }
  };

  const handleContextMenu = (e: MouseEvent) => {
    if (currentStep.value !== "complete") {
      e.preventDefault();
    }
  };

  document.addEventListener("keydown", handleKeyDown, true);
  window.addEventListener("beforeunload", handleBeforeUnload);
  document.addEventListener("contextmenu", handleContextMenu, true);

  (window as any).__preTestHandlers = {
    handleKeyDown,
    handleBeforeUnload,
    handleContextMenu,
  };

  logInfo("✅ Pre-test mandatory blocking activated");
};

const cleanupMandatoryBlocking = () => {
  isBlockingEscape.value = false;

  const handlers = (window as any).__preTestHandlers;
  if (handlers) {
    document.removeEventListener("keydown", handlers.handleKeyDown, true);
    window.removeEventListener("beforeunload", handlers.handleBeforeUnload);
    document.removeEventListener("contextmenu", handlers.handleContextMenu, true);
    delete (window as any).__preTestHandlers;
  }

  logDebug("✅ Pre-test mandatory blocking deactivated");
};

onBeforeUnmount(() => {
  cleanupMandatoryBlocking();
});

/**
 * ✅ PERFORMANCE FIX: Cached LaTeX rendering
 * Prevents re-rendering same LaTeX formulas multiple times
 */
function renderLatexCached(text: string): string {
  if (!text) return "";

  // Check cache first
  if (latexCache.has(text)) {
    return latexCache.get(text)!;
  }

  // Render and cache
  const rendered = renderLatex(text);
  latexCache.set(text, rendered);
  return rendered;
}

// Expose method to check pending pre-test from localStorage
defineExpose({
  checkPreTestCompleted,
});
</script>

<style scoped>
/* Prevent text selection */
.fixed {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.fixed * {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* ⚡ PERFORMANCE: Minimal backdrop blur for better performance */
.backdrop-blur-sm {
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

/* Prevent body scroll when modal is open */
body:has(.fixed.inset-0.z-\[9999\]) {
  overflow: hidden;
  height: 100vh;
  position: relative;
}

/* ⚡ PERFORMANCE: Ultra-simplified entrance animation */
@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fixed > div:not(.absolute) {
  animation: modalFadeIn 0.15s ease-out;
  will-change: opacity;
}

/* ✨ Enhanced Loading Screen Animations */
@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse-slow {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-spin-slow {
  animation: spin-slow 3s linear infinite;
}

.animate-pulse-slow {
  animation: pulse-slow 2s ease-in-out infinite;
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out forwards;
  opacity: 0;
}

/* ✅ KaTeX Formula Styling - Mobile Responsive */
:deep(.katex-display-wrapper) {
  margin: 0.75em 0;
  text-align: center;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

:deep(.katex-inline-wrapper) {
  display: inline;
}

:deep(.katex) {
  font-size: 1em;
  /* ⚡ PERFORMANCE: Prevent layout shifts */
  line-height: 1.5;
}

@media (min-width: 640px) {
  :deep(.katex) {
    font-size: 1.1em;
  }
}

:deep(.katex-display) {
  margin: 0;
  /* ⚡ PERFORMANCE: Optimize rendering */
  text-rendering: optimizeSpeed;
}

/* Mobile-specific LaTeX adjustments */
@media (max-width: 639px) {
  :deep(.katex) {
    font-size: 0.9em;
  }

  :deep(.katex-display-wrapper) {
    margin: 0.5em 0;
  }

  /* Ensure formulas don't overflow on small screens */
  .question-latex :deep(.katex-display),
  .option-latex :deep(.katex-display) {
    font-size: 0.85em;
  }
}

/* LaTeX Error Styling */
:deep(.latex-error) {
  color: #dc2626;
  background-color: #fee2e2;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875rem;
  display: inline-block;
  max-width: 100%;
  overflow-wrap: break-word;
}

/* ✅ Markdown Formatting Styles (for Gemini responses) */
:deep(strong) {
  font-weight: 700;
  color: inherit;
}

:deep(em) {
  font-style: italic;
  color: inherit;
}

:deep(code) {
  background-color: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-family: "Courier New", monospace;
  font-size: 0.9em;
  color: #1f2937;
}

/* ⚡ PERFORMANCE: Optimize scrolling performance */
.overflow-y-auto {
  -webkit-overflow-scrolling: touch;
  /* Removed GPU optimizations - they cause excessive GPU load and fan noise */
}

/* ⚡ PERFORMANCE: Reduce repaints on button interactions */
button {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

/* ✅ Retry Modal Title Styles (matches PostTestModal) */
:deep(.elevated-modal-title-no-padding),
:deep(.swal2-title.elevated-modal-title-no-padding),
:deep(h2.swal2-title.elevated-modal-title-no-padding) {
  --swal2-title-padding: 0 !important;
  padding: 0 !important;
  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 0 !important;
  margin: 0 !important;
  margin-bottom: 0.5rem !important;
}
</style>

<style>
/* ✅ GLOBAL OVERRIDE: Nuclear option to remove ALL title padding (matches PostTestModal) */
.elevated-modal-title-no-padding,
h2.swal2-title.elevated-modal-title-no-padding,
#swal2-title.elevated-modal-title-no-padding {
  --swal2-title-padding: 0 !important;
  padding: 0 !important;
  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 0 !important;
}
</style>
